<template>
  <b-container>
    <b-breadcrumb>
      <b-breadcrumb-item :to="{name: 'ward-list', params: { wardId: wardId}}">Back to {{ward ? ward.ward_name : 'Ward'}}</b-breadcrumb-item>
      <b-breadcrumb-item active>Twilio Configurations</b-breadcrumb-item>
    </b-breadcrumb>
    <h3>Twilio Configuration Helper</h3>
    
    <!-- Credential Error Alert -->
    <b-alert v-if="credentialError" variant="warning" dismissible @dismissed="credentialError = null" class="mb-4">
      <h5>Twilio Credentials Required</h5>
      <p>{{credentialError}}</p>
      <p class="mb-0">
        Please configure your Twilio Account SID and Auth Token in the ward settings before using this feature.
      </p>
    </b-alert>
    
    <!-- Setup Requirements Warning -->
    <b-alert 
      :show="showSetupWarningComputed && !services_table.isBusy && !profile_table.isBusy && !phone_numbers_table.isBusy" 
      variant="warning" 
      dismissible 
      @dismissed="showSetupWarning = false" 
      class="mb-4">
      <strong>Twilio Setup Required</strong><br>
      To use the application, you need:
      <ul class="mb-0 mt-2">
        <li v-if="!hasApprovedProfile">
          <strong>A Twilio-approved profile</strong> - Create a profile and complete the approval process (Steps 9-10)
        </li>
        <li v-if="!hasMessagingService">
          <strong>At least one messaging service</strong> - Create a messaging service to handle your messages
        </li>
        <li v-if="!hasPhoneNumber">
          <strong>At least one phone number</strong> - Ensure you have a phone number configured in your Twilio account
        </li>
      </ul>
    </b-alert>
    
    <!-- Tabs for Profiles and Messaging Services -->
    <b-tabs content-class="mt-3">
      <!-- Profiles Tab -->
      <b-tab title="Profiles" active>
        <!-- Create Profile Wizard -->
    <b-card class="mb-4" v-if="showWizard && !createdProfile">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">Create New Starter Profile</h4>
        <b-button variant="secondary" size="sm" @click="hideWizard">Cancel</b-button>
      </div>
      
      <!-- Step Indicator -->
      <b-nav pills class="mb-4">
        <b-nav-item :active="wizardStep === 0" @click="goToStep(0)">1. Profile Info</b-nav-item>
        <b-nav-item :active="wizardStep === 1" @click="goToStep(1)">2. Address</b-nav-item>
        <b-nav-item :active="wizardStep === 2" @click="goToStep(2)">3. Review</b-nav-item>
      </b-nav>
      
      <!-- Step 1: Profile Information -->
      <div v-show="wizardStep === 0">
          <b-form @submit.prevent>
            <b-form-group
              label="Friendly Name"
              label-for="friendly-name"
              :invalid-feedback="errors.friendly_name"
              :state="errors.friendly_name ? false : null">
              <b-form-input
                id="friendly-name"
                v-model="formData.friendly_name"
                placeholder="Enter profile friendly name"
                :state="errors.friendly_name ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="First Name"
              label-for="first-name"
              :invalid-feedback="errors.first_name"
              :state="errors.first_name ? false : null">
              <b-form-input
                id="first-name"
                v-model="formData.first_name"
                placeholder="Enter first name"
                :state="errors.first_name ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Last Name"
              label-for="last-name"
              :invalid-feedback="errors.last_name"
              :state="errors.last_name ? false : null">
              <b-form-input
                id="last-name"
                v-model="formData.last_name"
                placeholder="Enter last name"
                :state="errors.last_name ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Phone Number"
              label-for="phone-number"
              :invalid-feedback="errors.phone_number"
              :state="errors.phone_number ? false : null">
              <b-form-input
                id="phone-number"
                v-model="formData.phone_number"
                placeholder="Enter phone number"
                :state="errors.phone_number ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Email Address"
              label-for="email"
              :invalid-feedback="errors.email"
              :state="errors.email ? false : null">
              <b-form-input
                id="email"
                v-model="formData.email"
                type="email"
                placeholder="Enter email address"
                :state="errors.email ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Status Callback URL (Optional)"
              label-for="status-callback">
              <b-form-input
                id="status-callback"
                v-model="formData.status_callback"
                placeholder="Enter status callback URL (optional)">
              </b-form-input>
            </b-form-group>
          </b-form>
          <div class="mt-3">
            <b-button variant="primary" @click="nextStep">Next</b-button>
          </div>
      </div>
      
      <!-- Step 2: Address Information -->
      <div v-show="wizardStep === 1">
          <b-form @submit.prevent>
            <b-alert variant="info" class="mb-3">
              <strong>Option 1:</strong> Select an existing address or enter an address SID to reuse it.<br>
              <strong>Option 2:</strong> Leave address SID empty and fill in the address fields below to create a new address.
            </b-alert>
            
            <b-form-group
              label="Use Existing Address (Optional)"
              label-for="address-sid"
              description="Select an existing address from the list or enter an address SID directly">
              <b-form-select
                id="address-sid"
                v-model="formData.address_sid"
                :options="addressOptions"
                @change="onAddressSidChange">
                <template slot="first">
                  <option :value="null">-- Select an existing address or enter SID below --</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted mt-1">
                Or enter address SID manually:
              </small>
              <b-form-input
                v-model="formData.address_sid"
                placeholder="AD..."
                class="mt-2"
                @input="onAddressSidInput">
              </b-form-input>
              <b-button 
                v-if="!isLoadingAddresses"
                variant="link" 
                size="sm" 
                @click="loadAddresses"
                class="mt-2 p-0">
                Refresh Address List
              </b-button>
              <b-spinner v-if="isLoadingAddresses" small class="ml-2"></b-spinner>
            </b-form-group>
            
            <b-alert v-if="formData.address_sid" variant="success" class="mb-3">
              Using existing address: {{formData.address_sid}}. Address fields below are disabled.
            </b-alert>
            
            <b-form-group
              label="Street Address"
              label-for="street"
              :invalid-feedback="errors.street"
              :state="errors.street ? false : null">
              <b-form-input
                id="street"
                v-model="formData.street"
                placeholder="Enter street address"
                :state="errors.street ? false : null"
                :disabled="!!formData.address_sid"
                :required="!formData.address_sid">
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="City"
              label-for="city"
              :invalid-feedback="errors.city"
              :state="errors.city ? false : null">
              <b-form-input
                id="city"
                v-model="formData.city"
                placeholder="Enter city"
                :state="errors.city ? false : null"
                :disabled="!!formData.address_sid"
                :required="!formData.address_sid">
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Region/State"
              label-for="region"
              :invalid-feedback="errors.region"
              :state="errors.region ? false : null">
              <b-form-input
                id="region"
                v-model="formData.region"
                placeholder="Enter region/state"
                :state="errors.region ? false : null"
                :disabled="!!formData.address_sid"
                :required="!formData.address_sid">
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Postal Code"
              label-for="postal-code"
              :invalid-feedback="errors.postal_code"
              :state="errors.postal_code ? false : null">
              <b-form-input
                id="postal-code"
                v-model="formData.postal_code"
                placeholder="Enter postal code"
                :state="errors.postal_code ? false : null"
                :disabled="!!formData.address_sid"
                :required="!formData.address_sid">
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Country Code"
              label-for="iso-country"
              :invalid-feedback="errors.iso_country"
              :state="errors.iso_country ? false : null">
              <b-form-select
                id="iso-country"
                v-model="formData.iso_country"
                :options="countryOptions"
                :state="errors.iso_country ? false : null"
                :disabled="!!formData.address_sid"
                :required="!formData.address_sid">
              </b-form-select>
            </b-form-group>
          </b-form>
          <div class="mt-3">
            <b-button variant="secondary" @click="prevStep" class="mr-2">Previous</b-button>
            <b-button variant="primary" @click="nextStep">Next</b-button>
          </div>
      </div>
      
      <!-- Step 3: Review & Actions -->
      <div v-show="wizardStep === 2">
          <div>
            <h5>Review Information</h5>
            <b-card>
              <h6>Profile Information</h6>
              <p><strong>Friendly Name:</strong> {{formData.friendly_name}}</p>
              <p><strong>Name:</strong> {{formData.first_name}} {{formData.last_name}}</p>
              <p><strong>Phone:</strong> {{formData.phone_number}}</p>
              <p><strong>Email:</strong> {{formData.email}}</p>
              <p v-if="formData.status_callback"><strong>Status Callback:</strong> {{formData.status_callback}}</p>
            </b-card>
            <b-card class="mt-3">
              <h6>Address Information</h6>
              <p v-if="formData.address_sid">
                <strong>Using Existing Address:</strong> {{formData.address_sid}}
              </p>
              <template v-else>
                <p><strong>Street:</strong> {{formData.street}}</p>
                <p><strong>City:</strong> {{formData.city}}</p>
                <p><strong>Region:</strong> {{formData.region}}</p>
                <p><strong>Postal Code:</strong> {{formData.postal_code}}</p>
                <p><strong>Country:</strong> {{formData.iso_country}}</p>
              </template>
            </b-card>
            
            <div class="mt-4" v-if="createdProfile">
              <b-alert :show="!!creationMessage" :variant="creationVariant">
                {{creationMessage}}
              </b-alert>
              
              <div v-if="createdProfile.profile_sid">
                <b-button
                  variant="primary"
                  @click="runEvaluation"
                  :disabled="isProcessing"
                  class="mr-2">
                  Run Evaluation (Step 9)
                </b-button>
                <b-button
                  variant="success"
                  @click="submitForReview"
                  :disabled="isProcessing">
                  Submit for Review (Step 10)
                </b-button>
              </div>
            </div>
          </div>
          <div class="mt-3" v-if="!createdProfile">
            <b-button variant="secondary" @click="prevStep" class="mr-2">Previous</b-button>
            <b-button variant="success" @click="createProfile" :disabled="isProcessing">
              <b-spinner small v-if="isProcessing" class="mr-1"></b-spinner>
              Create Profile
            </b-button>
          </div>
      </div>
    </b-card>
    
    <!-- Existing Profiles Table -->
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5>Existing Profiles</h5>
        <div>
          <b-button v-if="!showWizard && !createdProfile" variant="primary" @click="showWizard = true" class="mr-2">
            Create Starter Profile
          </b-button>
          <b-button v-if="createdProfile" variant="primary" @click="resetWizard">Create Another Profile</b-button>
        </div>
      </div>
      <b-table
        :fields="profile_table.fields"
        :items="profile_table.items"
        :busy="profile_table.isBusy"
        light
        responsive
        show-empty
        empty-text="No Profiles to Show"
        outlined>
        <template slot="table-busy">
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong> Loading...</strong>
          </div>
        </template>
        <template slot="cell(actions)" slot-scope="row">
          <b-button 
            variant="info" 
            size="sm" 
            @click="viewProfileDetails(row.item)"
            class="mr-2">
            View Details
          </b-button>
          <b-button 
            v-if="isProfileDraft(row.item)"
            variant="danger" 
            size="sm" 
            @click="confirmDeleteProfile(row.item)">
            Delete
          </b-button>
        </template>
      </b-table>
    </b-card>
    
    <!-- Profile Details Modal -->
    <b-modal 
      id="profile-details-modal" 
      title="Profile Details" 
      size="lg"
      @hide="selectedProfile = null; showProfileDetails = false; showEvaluations = false; profileEvaluations = [];">
      <div v-if="selectedProfile">
        <b-card>
          <h6>Basic Information</h6>
          <p><strong>Profile SID:</strong> {{selectedProfile.sid || 'N/A'}}</p>
          <p><strong>Friendly Name:</strong> {{selectedProfile.friendly_name || 'N/A'}}</p>
          <p><strong>Account SID:</strong> {{selectedProfile.account_sid || 'N/A'}}</p>
          <p><strong>Status:</strong> 
            <b-badge 
              :variant="getProfileStatusVariant(selectedProfile.status)"
              class="ml-1">
              {{selectedProfile.status || 'N/A'}}
            </b-badge>
          </p>
          <p><strong>Email:</strong> {{selectedProfile.email || 'N/A'}}</p>
          <p><strong>Date Created:</strong> {{formatDate(selectedProfile.date_created)}}</p>
          <p><strong>Date Updated:</strong> {{formatDate(selectedProfile.date_updated)}}</p>
          <p v-if="selectedProfile.valid_until"><strong>Valid Until:</strong> {{formatDate(selectedProfile.valid_until)}}</p>
          <p v-if="selectedProfile.url"><strong>URL:</strong> {{selectedProfile.url}}</p>
        </b-card>
        
        <b-card class="mt-3" v-if="selectedProfile.policy_sid || selectedProfile.status_callback">
          <div 
            @click="showProfileDetails = !showProfileDetails" 
            style="cursor: pointer; user-select: none;"
            class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Additional Information</h6>
            <span>{{ showProfileDetails ? '▼' : '▶' }}</span>
          </div>
          <b-collapse v-model="showProfileDetails" class="mt-2">
            <p v-if="selectedProfile.policy_sid"><strong>Policy SID:</strong> {{selectedProfile.policy_sid}}</p>
            <p v-if="selectedProfile.status_callback"><strong>Status Callback:</strong> {{selectedProfile.status_callback}}</p>
            <p v-if="selectedProfile.errors"><strong>Errors:</strong> {{selectedProfile.errors}}</p>
            <div v-if="selectedProfile.attributes">
              <strong>Attributes:</strong>
              <pre class="mt-2 p-2 bg-light border rounded" style="max-height: 300px; overflow-y: auto;">{{JSON.stringify(selectedProfile.attributes, null, 2)}}</pre>
            </div>
            <div v-if="selectedProfile.links" class="mt-3">
              <strong>Links:</strong>
              <pre class="mt-2 p-2 bg-light border rounded" style="max-height: 300px; overflow-y: auto;">{{JSON.stringify(selectedProfile.links, null, 2)}}</pre>
            </div>
          </b-collapse>
        </b-card>
        
        <b-card class="mt-3">
          <div 
            @click="toggleEvaluations" 
            style="cursor: pointer; user-select: none;"
            class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Evaluations</h6>
            <span>{{ showEvaluations ? '▼' : '▶' }}</span>
          </div>
          <b-collapse v-model="showEvaluations" class="mt-2">
            <div v-if="isLoadingEvaluations" class="text-center my-3">
              <b-spinner small></b-spinner>
              <span class="ml-2">Loading evaluations...</span>
            </div>
            <div v-else-if="profileEvaluations.length === 0" class="text-muted my-3">
              No evaluations found for this profile.
            </div>
            <div v-else>
              <b-table
                :items="profileEvaluations"
                :fields="[
                  { key: 'sid', label: 'Evaluation SID' },
                  { key: 'status', label: 'Status' },
                  { key: 'policy_sid', label: 'Policy SID' },
                  { key: 'date_created', label: 'Date Created' }
                ]"
                small
                striped
                responsive>
                <template slot="cell(status)" slot-scope="row">
                  <b-badge :variant="getEvaluationStatusVariant(row.item.status)">
                    {{row.item.status || 'N/A'}}
                  </b-badge>
                </template>
                <template slot="cell(date_created)" slot-scope="row">
                  {{formatDate(row.item.date_created)}}
                </template>
              </b-table>
            </div>
          </b-collapse>
        </b-card>
      </div>
    </b-modal>
      </b-tab>
      
      <!-- Messaging Services Tab -->
      <b-tab title="Messaging Services">
        <!-- Services Table -->
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>Messaging Services</h5>
            <div>
              <b-button 
                variant="primary" 
                @click="openCreateServiceModal" 
                class="mr-2">
                Create Service
              </b-button>
            </div>
          </div>
          <b-table
            :fields="services_table.fields"
            :items="services_table.items"
            :busy="services_table.isBusy"
            light
            responsive
            show-empty
            empty-text="No Messaging Services to Show"
            outlined>
            <template slot="table-busy">
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong> Loading...</strong>
              </div>
            </template>
            <template slot="cell(actions)" slot-scope="row">
              <b-button 
                variant="info" 
                size="sm" 
                @click="viewServiceDetails(row.item)"
                class="mr-2">
                View Details
              </b-button>
              <b-button 
                variant="primary" 
                size="sm" 
                @click="openAddPhoneToServiceModal(row.item)"
                class="mr-2">
                Add Phone
              </b-button>
              <b-button 
                variant="secondary" 
                size="sm" 
                @click="connectCampaign(row.item)">
                Connect Campaign
              </b-button>
            </template>
          </b-table>
        </b-card>
        
        <!-- Service Details Modal -->
        <b-modal 
          id="service-details-modal" 
          title="Messaging Service Details" 
          size="lg"
          @hide="selectedService = null; showConfiguration = false; showFeatures = false; showLinks = false; showServicePhoneNumbers = false; servicePhoneNumbers = []; showServiceCampaigns = false; serviceCampaigns = []; showServiceUsa2p = false; serviceUsa2pResources = [];">
          <div v-if="selectedService">
            <b-card>
              <h6>Basic Information</h6>
              <p><strong>Service SID:</strong> {{selectedService.sid}}</p>
              <p><strong>Friendly Name:</strong> {{selectedService.friendly_name || 'N/A'}}</p>
              <p><strong>Account SID:</strong> {{selectedService.account_sid || 'N/A'}}</p>
              <p><strong>Date Created:</strong> {{formatDate(selectedService.date_created)}}</p>
              <p><strong>Date Updated:</strong> {{formatDate(selectedService.date_updated)}}</p>
              <p v-if="selectedService.url"><strong>URL:</strong> {{selectedService.url}}</p>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showConfiguration = !showConfiguration" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Configuration</h6>
                <span>{{ showConfiguration ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showConfiguration" class="mt-2">
                <p><strong>Inbound Request URL:</strong> {{selectedService.inbound_request_url || 'Not set'}}</p>
                <p><strong>Inbound Method:</strong> {{selectedService.inbound_method || 'Not set'}}</p>
                <p><strong>Fallback URL:</strong> {{selectedService.fallback_url || 'Not set'}}</p>
                <p><strong>Fallback Method:</strong> {{selectedService.fallback_method || 'Not set'}}</p>
                <p><strong>Status Callback:</strong> {{selectedService.status_callback || 'Not set'}}</p>
              </b-collapse>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showFeatures = !showFeatures" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Features</h6>
                <span>{{ showFeatures ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showFeatures" class="mt-2">
                <p><strong>Sticky Sender:</strong> {{selectedService.sticky_sender !== null ? (selectedService.sticky_sender ? 'Enabled' : 'Disabled') : 'N/A'}}</p>
                <p><strong>Smart Encoding:</strong> {{selectedService.smart_encoding !== null ? (selectedService.smart_encoding ? 'Enabled' : 'Disabled') : 'N/A'}}</p>
                <p><strong>Scan Message Content:</strong> {{selectedService.scan_message_content || 'N/A'}}</p>
                <p><strong>Area Code Geo-match:</strong> {{selectedService.area_code_geomatch !== null ? (selectedService.area_code_geomatch ? 'Enabled' : 'Disabled') : 'N/A'}}</p>
                <p><strong>Validity Period:</strong> {{selectedService.validity_period !== null ? selectedService.validity_period + ' seconds' : 'N/A'}}</p>
                <p><strong>Synchronous Validation:</strong> {{selectedService.synchronous_validation !== null ? (selectedService.synchronous_validation ? 'Enabled' : 'Disabled') : 'N/A'}}</p>
              </b-collapse>
            </b-card>
            
            <b-card v-if="selectedService.links" class="mt-3">
              <div 
                @click="showLinks = !showLinks" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Links</h6>
                <span>{{ showLinks ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showLinks" class="mt-2">
                <pre>{{JSON.stringify(selectedService.links, null, 2)}}</pre>
              </b-collapse>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showServicePhoneNumbers = !showServicePhoneNumbers" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Associated Phone Numbers <span v-if="servicePhoneNumbers.length > 0">({{servicePhoneNumbers.length}})</span></h6>
                <span>{{ showServicePhoneNumbers ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showServicePhoneNumbers" class="mt-2" @show="loadServicePhoneNumbers">
                <div v-if="isLoadingServicePhoneNumbers" class="text-center my-2">
                  <b-spinner small></b-spinner>
                  <span class="ml-2">Loading phone numbers...</span>
                </div>
                <div v-else-if="servicePhoneNumbers.length > 0">
                  <b-table
                    :fields="servicePhoneNumbersTableFields"
                    :items="servicePhoneNumbers"
                    light
                    responsive
                    small
                    outlined>
                    <template slot="cell(phone_number)" slot-scope="row">
                      <strong>{{row.item.phone_number || 'N/A'}}</strong>
                    </template>
                    <template slot="cell(capabilities)" slot-scope="row">
                      <span v-if="row.item.capabilities && row.item.capabilities.length > 0">
                        <b-badge v-for="cap in row.item.capabilities" :key="cap" variant="info" class="mr-1">{{cap}}</b-badge>
                      </span>
                      <span v-else>N/A</span>
                    </template>
                    <template slot="cell(actions)" slot-scope="row">
                      <b-button 
                        variant="danger" 
                        size="sm" 
                        @click="removePhoneFromService(row.item.sid)"
                        :disabled="isRemovingPhoneFromService">
                        <b-spinner small v-if="isRemovingPhoneFromService" class="mr-1"></b-spinner>
                        Remove
                      </b-button>
                    </template>
                  </b-table>
                </div>
                <div v-else>
                  <p class="text-muted mb-0">No phone numbers associated with this service.</p>
                </div>
              </b-collapse>
            </b-card>
            
            <!-- Associated Campaigns Section -->
            <b-card class="mt-3">
              <div 
                @click="showServiceCampaigns = !showServiceCampaigns" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Associated Campaigns <span v-if="serviceCampaigns.length > 0">({{serviceCampaigns.length}})</span></h6>
                <span>{{ showServiceCampaigns ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showServiceCampaigns" class="mt-2" @show="loadServiceCampaigns">
                <div v-if="isLoadingServiceCampaigns" class="text-center my-2">
                  <b-spinner small></b-spinner>
                  <span class="ml-2">Loading campaigns...</span>
                </div>
                <div v-else-if="serviceCampaigns.length > 0">
                  <b-table
                    :fields="[
                      { key: 'campaign_id', label: 'Campaign ID' },
                      { key: 'use_case', label: 'Use Case' },
                      { key: 'status', label: 'Status' },
                      { key: 'date_created', label: 'Created' },
                      { key: 'actions', label: 'Actions' }
                    ]"
                    :items="serviceCampaigns"
                    light
                    responsive
                    small
                    outlined>
                    <template slot="cell(use_case)" slot-scope="row">
                      <span>{{row.item.use_case || 'Sole Proprietorship'}}</span>
                    </template>
                    <template slot="cell(status)" slot-scope="row">
                      <b-badge :variant="getCampaignStatusVariant(row.item.status)">
                        {{row.item.status || 'N/A'}}
                      </b-badge>
                    </template>
                    <template slot="cell(date_created)" slot-scope="row">
                      {{formatDate(row.item.date_created)}}
                    </template>
                    <template slot="cell(actions)" slot-scope="row">
                      <b-button 
                        variant="primary" 
                        size="sm" 
                        @click="viewCampaignDetails(row.item)"
                        class="mr-1">
                        View Details
                      </b-button>
                    </template>
                  </b-table>
                </div>
                <div v-else>
                  <p class="text-muted mb-0">No campaigns associated with this service.</p>
                </div>
              </b-collapse>
            </b-card>
            
            <!-- A2P 10DLC Campaigns (Usa2p) Section -->
            <b-card class="mt-3">
              <div 
                @click="showServiceUsa2p = !showServiceUsa2p" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">A2P 10DLC Campaigns (Usa2p) <span v-if="serviceUsa2pResources.length > 0">({{serviceUsa2pResources.length}})</span></h6>
                <span>{{ showServiceUsa2p ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showServiceUsa2p" class="mt-2" @show="loadServiceUsa2pResources">
                <div class="mb-2">
                  <b-button 
                    variant="primary" 
                    size="sm" 
                    @click="openCreateUsa2pModal(selectedService)"
                    class="mb-2">
                    Create Usa2p Resource
                  </b-button>
                </div>
                <div v-if="isLoadingServiceUsa2p" class="text-center my-2">
                  <b-spinner small></b-spinner>
                  <span class="ml-2">Loading usa2p resources...</span>
                </div>
                <div v-else-if="serviceUsa2pResources.length > 0">
                  <b-table
                    :fields="[
                      { key: 'campaign_id', label: 'Campaign ID' },
                      { key: 'us_app_to_person_usecase', label: 'Use Case' },
                      { key: 'campaign_status', label: 'Status' },
                      { key: 'brand_registration_sid', label: 'Brand SID' },
                      { key: 'date_created', label: 'Created' },
                      { key: 'actions', label: 'Actions' }
                    ]"
                    :items="serviceUsa2pResources"
                    light
                    responsive
                    small
                    outlined>
                    <template slot="cell(us_app_to_person_usecase)" slot-scope="row">
                      <span>{{row.item.us_app_to_person_usecase || 'N/A'}}</span>
                    </template>
                    <template slot="cell(campaign_status)" slot-scope="row">
                      <b-badge :variant="getUsa2pStatusVariant(row.item.campaign_status)">
                        {{row.item.campaign_status || 'N/A'}}
                      </b-badge>
                    </template>
                    <template slot="cell(date_created)" slot-scope="row">
                      {{formatDate(row.item.date_created)}}
                    </template>
                    <template slot="cell(actions)" slot-scope="row">
                      <b-button 
                        variant="primary" 
                        size="sm" 
                        @click="viewUsa2pDetails(row.item)"
                        class="mr-1">
                        View Details
                      </b-button>
                      <b-button 
                        variant="danger" 
                        size="sm" 
                        @click="deleteUsa2pResource(row.item)"
                        :disabled="isDeletingUsa2p">
                        Delete
                      </b-button>
                    </template>
                  </b-table>
                </div>
                <div v-else>
                  <p class="text-muted mb-0">No usa2p resources associated with this service.</p>
                </div>
              </b-collapse>
            </b-card>
          </div>
        </b-modal>
        
        <!-- Add Phone to Service Modal -->
        <b-modal 
          id="add-phone-to-service-modal" 
          title="Add Phone Number to Service" 
          size="lg"
          @hide="resetAddPhoneToServiceForm"
          @ok.prevent="addPhoneToService">
          <div v-if="selectedServiceForAddPhone">
            <p><strong>Service:</strong> {{selectedServiceForAddPhone.friendly_name || selectedServiceForAddPhone.sid}}</p>
            <b-form-group
              label="Select Phone Number"
              label-for="phone-select"
              :invalid-feedback="addPhoneToServiceErrors.phone_number_sid"
              :state="addPhoneToServiceErrors.phone_number_sid ? false : null">
              <b-form-select
                id="phone-select"
                v-model="addPhoneToServiceForm.phone_number_sid"
                :options="availablePhoneNumbersForService"
                :state="addPhoneToServiceErrors.phone_number_sid ? false : null"
                required>
                <template slot="first">
                  <option :value="null">-- Select a phone number --</option>
                </template>
              </b-form-select>
              <small class="form-text text-muted">
                Only phone numbers not already in this service are shown.
              </small>
            </b-form-group>
          </div>
          <template slot="modal-footer">
            <b-button variant="secondary" @click="$bvModal.hide('add-phone-to-service-modal')">Cancel</b-button>
            <b-button variant="primary" @click="addPhoneToService" :disabled="isAddingPhoneToService">
              <b-spinner small v-if="isAddingPhoneToService" class="mr-1"></b-spinner>
              Add Phone Number
            </b-button>
          </template>
        </b-modal>
        
        <!-- Create Service Modal -->
        <b-modal 
          id="create-service-modal" 
          title="Create Messaging Service" 
          size="lg"
          @hide="resetCreateServiceForm"
          @ok.prevent="createService">
          <b-form @submit.stop.prevent="createService">
            <b-form-group
              label="Friendly Name"
              label-for="service-friendly-name"
              :invalid-feedback="serviceFormErrors.friendly_name"
              :state="serviceFormErrors.friendly_name ? false : null">
              <b-form-input
                id="service-friendly-name"
                v-model="serviceFormData.friendly_name"
                placeholder="Enter service friendly name"
                :state="serviceFormErrors.friendly_name ? false : null"
                required>
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Inbound Request URL"
              label-for="service-inbound-url">
              <b-form-input
                id="service-inbound-url"
                :value="inboundRequestUrl"
                disabled
                readonly>
              </b-form-input>
              <small class="form-text text-muted">
                This URL is automatically set to receive inbound messages for this ward.
              </small>
            </b-form-group>
          </b-form>
          <template slot="modal-footer">
            <b-button variant="secondary" @click="$bvModal.hide('create-service-modal')">Cancel</b-button>
            <b-button variant="primary" @click="createService" :disabled="isCreatingService">
              <b-spinner small v-if="isCreatingService" class="mr-1"></b-spinner>
              Create Service
            </b-button>
          </template>
        </b-modal>
        
        <!-- Create Brand Registration Modal -->
        <b-modal 
          id="create-brand-registration-modal" 
          v-model="showCreateBrandRegistrationModal"
          title="Create Brand Registration" 
          size="lg"
          @hide="resetCreateBrandRegistrationForm"
          @ok.prevent="createBrandRegistration">
          <b-alert variant="info" class="mb-3">
            <strong>Note:</strong> Brand registrations link customer profiles to A2P 10DLC messaging. You need an approved customer profile with a bundle to create a brand registration.
          </b-alert>
          
          <b-form @submit.stop.prevent="createBrandRegistration">
            <b-form-group
              label="Customer Profile Bundle SID"
              label-for="brand-customer-profile-bundle"
              :invalid-feedback="createBrandRegistrationErrors.customer_profile_bundle_sid"
              :state="createBrandRegistrationErrors.customer_profile_bundle_sid ? false : null"
              description="Enter the bundle SID (BU...) from your approved customer profile. You can find this in existing brand registrations or the Twilio console.">
              <b-form-input
                id="brand-customer-profile-bundle"
                v-model="createBrandRegistrationForm.customer_profile_bundle_sid"
                placeholder="BU..."
                :state="createBrandRegistrationErrors.customer_profile_bundle_sid ? false : null"
                required>
              </b-form-input>
              <small class="form-text text-muted mt-1">
                Bundle SID format: BU followed by 32 hexadecimal characters (e.g., BU3344409f7e067e279523808d267e2d85)
              </small>
            </b-form-group>
            
            <b-form-group
              label="A2P Profile Bundle SID (Optional)"
              label-for="brand-a2p-profile-bundle"
              :invalid-feedback="createBrandRegistrationErrors.a2p_profile_bundle_sid"
              :state="createBrandRegistrationErrors.a2p_profile_bundle_sid ? false : null">
              <b-form-input
                id="brand-a2p-profile-bundle"
                v-model="createBrandRegistrationForm.a2p_profile_bundle_sid"
                placeholder="Enter A2P profile bundle SID (optional)"
                :state="createBrandRegistrationErrors.a2p_profile_bundle_sid ? false : null">
              </b-form-input>
            </b-form-group>
            
            <b-form-group
              label="Brand Type (Optional)"
              label-for="brand-type"
              description="STANDARD for multiple campaign use cases, SOLE_PROPRIETOR for low volume sole proprietorship">
              <b-form-select
                id="brand-type"
                v-model="createBrandRegistrationForm.brand_type"
                :options="[
                  { value: null, text: '-- Select brand type (optional) --' },
                  { value: 'STANDARD', text: 'STANDARD' },
                  { value: 'SOLE_PROPRIETOR', text: 'SOLE_PROPRIETOR' }
                ]">
              </b-form-select>
            </b-form-group>
            
            <b-form-group>
              <b-form-checkbox v-model="createBrandRegistrationForm.mock">
                Mock Brand Registration
              </b-form-checkbox>
              <small class="form-text text-muted">Use for testing purposes only</small>
            </b-form-group>
            
            <b-form-group>
              <b-form-checkbox v-model="createBrandRegistrationForm.skip_automatic_sec_vet">
                Skip Automatic Security Vetting
              </b-form-checkbox>
            </b-form-group>
          </b-form>
          
          <template slot="modal-footer">
            <b-button variant="secondary" @click="showCreateBrandRegistrationModal = false; resetCreateBrandRegistrationForm()">Cancel</b-button>
            <b-button variant="primary" @click="createBrandRegistration" :disabled="isCreatingBrandRegistration">
              <b-spinner small v-if="isCreatingBrandRegistration" class="mr-1"></b-spinner>
              Create Brand Registration
            </b-button>
          </template>
        </b-modal>
        
        <!-- Brand Details Modal -->
        <b-modal 
          id="brand-details-modal" 
          title="Brand Registration Details" 
          size="lg"
          @hide="selectedBrand = null;">
          <div v-if="selectedBrand">
            <b-card>
              <h6>Basic Information</h6>
              <p><strong>Brand SID:</strong> {{selectedBrand.sid}}</p>
              <p><strong>Brand Type:</strong> {{selectedBrand.brand_type || 'N/A'}}</p>
              <p><strong>Status:</strong> 
                <b-badge :variant="getBrandStatusVariant(selectedBrand.status)">
                  {{selectedBrand.status || 'N/A'}}
                </b-badge>
              </p>
              <p><strong>TCR ID:</strong> {{selectedBrand.tcr_id || 'N/A'}}</p>
              <p><strong>Brand Score:</strong> {{selectedBrand.brand_score !== null && selectedBrand.brand_score !== undefined ? selectedBrand.brand_score : 'N/A'}}</p>
              <p><strong>Customer Profile Bundle SID:</strong> {{selectedBrand.customer_profile_bundle_sid || 'N/A'}}</p>
              <p><strong>A2P Profile Bundle SID:</strong> {{selectedBrand.a2p_profile_bundle_sid || 'N/A'}}</p>
              <p><strong>Date Created:</strong> {{formatDate(selectedBrand.date_created)}}</p>
              <p><strong>Date Updated:</strong> {{formatDate(selectedBrand.date_updated)}}</p>
            </b-card>
            
            <b-card class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">Actions</h6>
              </div>
              <b-button 
                variant="info" 
                size="sm"
                @click="openCreateUsa2pModal(null, selectedBrand)"
                :disabled="selectedBrand.status !== 'APPROVED'">
                Create Usa2p Resource for this Brand
              </b-button>
              <small class="d-block text-muted mt-2" v-if="selectedBrand.status !== 'APPROVED'">
                Brand must be approved before creating usa2p resources
              </small>
            </b-card>
            
            <b-card class="mt-3" v-if="selectedBrand.brand_feedback && selectedBrand.brand_feedback.length > 0">
              <h6>Brand Feedback</h6>
              <ul>
                <li v-for="(feedback, index) in selectedBrand.brand_feedback" :key="index">{{feedback}}</li>
              </ul>
            </b-card>
            
            <b-card class="mt-3">
              <h6>Additional Information</h6>
              <p><strong>Identity Status:</strong> {{selectedBrand.identity_status || 'N/A'}}</p>
              <p><strong>Russell 3000:</strong> {{selectedBrand.russell_3000 ? 'Yes' : 'No'}}</p>
              <p><strong>Government Entity:</strong> {{selectedBrand.government_entity ? 'Yes' : 'No'}}</p>
              <p><strong>Tax Exempt Status:</strong> {{selectedBrand.tax_exempt_status || 'N/A'}}</p>
              <p><strong>Mock:</strong> {{selectedBrand.mock ? 'Yes' : 'No'}}</p>
            </b-card>
            
            <b-card class="mt-3" v-if="selectedBrand.errors && selectedBrand.errors.length > 0">
              <h6>Errors</h6>
              <ul>
                <li v-for="(error, index) in selectedBrand.errors" :key="index">{{JSON.stringify(error)}}</li>
              </ul>
            </b-card>
            
            <b-card class="mt-3" v-if="selectedBrand.failure_reason">
              <h6>Failure Reason</h6>
              <p class="text-danger">{{selectedBrand.failure_reason}}</p>
            </b-card>
          </div>
          
          <template slot="modal-footer">
            <b-button variant="secondary" @click="$bvModal.hide('brand-details-modal')">Close</b-button>
          </template>
        </b-modal>
        
        <!-- Campaign Details Modal -->
        <b-modal 
          id="campaign-details-modal" 
          title="Campaign Details" 
          size="lg"
          @hide="selectedCampaign = null">
          <div v-if="selectedCampaign">
            <b-card>
              <h6>Basic Information</h6>
              <p><strong>Campaign SID:</strong> {{selectedCampaign.sid}}</p>
              <p><strong>Campaign ID:</strong> {{selectedCampaign.campaign_id || 'N/A'}}</p>
              <p><strong>Use Case:</strong> {{selectedCampaign.use_case || 'Sole Proprietorship'}}</p>
              <p><strong>Status:</strong> 
                <b-badge :variant="getCampaignStatusVariant(selectedCampaign.status)">
                  {{selectedCampaign.status || 'N/A'}}
                </b-badge>
              </p>
              <p><strong>Date Created:</strong> {{formatDate(selectedCampaign.date_created)}}</p>
              <p><strong>Date Updated:</strong> {{formatDate(selectedCampaign.date_updated)}}</p>
            </b-card>
            
            <b-card class="mt-3">
              <h6>Campaign Details</h6>
              <p><strong>Description:</strong></p>
              <p>{{selectedCampaign.description || 'N/A'}}</p>
              
              <p class="mt-3"><strong>Sample Messages:</strong></p>
              <ul v-if="selectedCampaign.sample_messages && selectedCampaign.sample_messages.length > 0">
                <li v-for="(message, index) in selectedCampaign.sample_messages" :key="index">{{message}}</li>
              </ul>
              <p v-else class="text-muted">No sample messages</p>
              
              <p class="mt-3"><strong>Opt-in Process:</strong></p>
              <p>{{selectedCampaign.opt_in_message || selectedCampaign.opt_in_process || 'N/A'}}</p>
            </b-card>
            
            <b-card class="mt-3" v-if="selectedCampaign.messaging_service_sid">
              <h6>Associated Messaging Service</h6>
              <p><strong>Service SID:</strong> {{selectedCampaign.messaging_service_sid}}</p>
            </b-card>
          </div>
          
          <template slot="modal-footer">
            <b-button 
              v-if="selectedCampaign && selectedCampaign.status && selectedCampaign.status.toLowerCase() === 'rejected'"
              variant="warning" 
              @click="resubmitCampaign" 
              :disabled="isResubmittingCampaign"
              class="mr-auto">
              <b-spinner small v-if="isResubmittingCampaign" class="mr-1"></b-spinner>
              Resubmit Campaign
            </b-button>
            <b-button variant="danger" @click="deleteCampaign" :disabled="isDeletingCampaign">
              <b-spinner small v-if="isDeletingCampaign" class="mr-1"></b-spinner>
              Delete Campaign
            </b-button>
            <b-button variant="secondary" @click="$bvModal.hide('campaign-details-modal')">Close</b-button>
          </template>
        </b-modal>
      </b-tab>
      
      <!-- Phone Numbers Tab -->
      <b-tab title="Phone Numbers">
        <!-- Add Phone Number Wizard -->
        <b-card class="mb-4" v-if="showPhoneNumberWizard">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0">Add Phone Number</h4>
            <b-button variant="secondary" size="sm" @click="resetPhoneNumberWizard">Cancel</b-button>
          </div>
          
          <!-- Step Indicator -->
          <b-nav pills class="mb-4">
            <b-nav-item :active="phoneSearchStep === 0" @click="goToPhoneSearchStep(0)">1. Search Numbers</b-nav-item>
            <b-nav-item :active="phoneSearchStep === 1" @click="goToPhoneSearchStep(1)" :disabled="phoneSearchStep === 0 && phoneSearchResults.length === 0">2. Select & Purchase</b-nav-item>
          </b-nav>
          
          <!-- Step 1: Search Form -->
          <div v-show="phoneSearchStep === 0">
            <b-form @submit.prevent="searchAvailablePhoneNumbers">
              <b-form-group
                label="Country Code"
                label-for="phone-search-country"
                :invalid-feedback="phoneSearchErrors.country_code"
                :state="phoneSearchErrors.country_code ? false : null">
                <b-form-select
                  id="phone-search-country"
                  v-model="phoneSearchForm.country_code"
                  :options="countryCodeOptions"
                  :state="phoneSearchErrors.country_code ? false : null"
                  required>
                </b-form-select>
              </b-form-group>
              
              <b-form-group
                label="Area Code (Optional)"
                label-for="phone-search-area-code"
                description="For US/Canada numbers only">
                <b-form-input
                  id="phone-search-area-code"
                  v-model.number="phoneSearchForm.area_code"
                  type="number"
                  placeholder="e.g., 415">
                </b-form-input>
              </b-form-group>
              
              <b-form-group
                label="Contains Pattern (Optional)"
                label-for="phone-search-contains"
                description="Pattern to match in phone number (e.g., '555', '+1510')">
                <b-form-input
                  id="phone-search-contains"
                  v-model="phoneSearchForm.contains"
                  placeholder="e.g., 555">
                </b-form-input>
              </b-form-group>
              
              <b-form-group>
                <b-form-checkbox v-model="phoneSearchForm.sms_enabled">
                  SMS Enabled (default: checked)
                </b-form-checkbox>
              </b-form-group>
              
              <b-form-group
                label="Results Limit"
                label-for="phone-search-limit">
                <b-form-select
                  id="phone-search-limit"
                  v-model.number="phoneSearchForm.limit"
                  :options="searchLimitOptions">
                </b-form-select>
              </b-form-group>
              
              <b-button 
                type="submit" 
                variant="primary" 
                :disabled="isSearchingNumbers">
                <b-spinner small v-if="isSearchingNumbers" class="mr-1"></b-spinner>
                Search Numbers
              </b-button>
            </b-form>
          </div>
          
          <!-- Step 2: Results & Purchase -->
          <div v-show="phoneSearchStep === 1">
            <div v-if="phoneSearchResults.length > 0">
              <h5>Available Phone Numbers</h5>
              <b-table
                :fields="availableNumbersTableFields"
                :items="phoneSearchResults"
                light
                responsive
                outlined
                class="mb-3">
                <template slot="cell(phone_number)" slot-scope="row">
                  <strong>{{row.item.phone_number || 'N/A'}}</strong>
                </template>
                <template slot="cell(location)" slot-scope="row">
                  <span v-if="row.item.locality || row.item.region">
                    {{row.item.locality || ''}}{{row.item.locality && row.item.region ? ', ' : ''}}{{row.item.region || ''}}
                  </span>
                  <span v-else>N/A</span>
                </template>
                <template slot="cell(capabilities)" slot-scope="row">
                  <span v-if="row.item.capabilities">
                    <b-badge v-if="row.item.capabilities.voice" variant="info" class="mr-1">Voice</b-badge>
                    <b-badge v-if="row.item.capabilities.sms" variant="success" class="mr-1">SMS</b-badge>
                    <b-badge v-if="row.item.capabilities.mms" variant="warning" class="mr-1">MMS</b-badge>
                  </span>
                  <span v-else>N/A</span>
                </template>
                <template slot="cell(address_requirements)" slot-scope="row">
                  <b-badge v-if="row.item.address_requirements === 'none'" variant="success">None</b-badge>
                  <b-badge v-else-if="row.item.address_requirements" variant="warning">{{row.item.address_requirements}}</b-badge>
                  <span v-else>N/A</span>
                </template>
                <template slot="cell(actions)" slot-scope="row">
                  <b-button 
                    variant="primary" 
                    size="sm" 
                    @click="selectPhoneForPurchase(row.item)">
                    Select
                  </b-button>
                </template>
              </b-table>
              
              <b-button variant="secondary" @click="goToPhoneSearchStep(0)">Back to Search</b-button>
            </div>
            <div v-else>
              <b-alert variant="info">
                No phone numbers found. Try adjusting your search criteria.
              </b-alert>
              <b-button variant="secondary" @click="goToPhoneSearchStep(0)">Back to Search</b-button>
            </div>
            
            <!-- Purchase Confirmation Form -->
            <div v-if="selectedPhoneForPurchase" class="mt-4">
              <b-card>
                <h6>Purchase Phone Number</h6>
                <p><strong>Selected Number:</strong> {{selectedPhoneForPurchase.phone_number}}</p>
                <p v-if="selectedPhoneForPurchase.locality || selectedPhoneForPurchase.region">
                  <strong>Location:</strong> {{selectedPhoneForPurchase.locality || ''}}{{selectedPhoneForPurchase.locality && selectedPhoneForPurchase.region ? ', ' : ''}}{{selectedPhoneForPurchase.region || ''}}
                </p>
                
                <b-form @submit.prevent="purchasePhoneNumber" class="mt-3">
                  <b-form-group
                    label="SMS Webhook URL"
                    label-for="purchase-sms-url"
                    :invalid-feedback="purchasePhoneErrors.sms_url"
                    :state="purchasePhoneErrors.sms_url ? false : null"
                    description="The webhook URL where Twilio will send SMS messages">
                    <b-input-group>
                      <b-form-input
                        id="purchase-sms-url"
                        v-model="purchasePhoneForm.sms_url"
                        placeholder="Enter SMS webhook URL"
                        :state="purchasePhoneErrors.sms_url ? false : null">
                      </b-form-input>
                      <b-input-group-append>
                        <b-button 
                          variant="outline-secondary" 
                          @click="useWardWebhookForPurchase"
                          title="Use ward webhook URL">
                          Use Ward Webhook
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>
                  
                  <b-form-group
                    label="SMS Method"
                    label-for="purchase-sms-method"
                    :invalid-feedback="purchasePhoneErrors.sms_method"
                    :state="purchasePhoneErrors.sms_method ? false : null">
                    <b-form-select
                      id="purchase-sms-method"
                      v-model="purchasePhoneForm.sms_method"
                      :options="smsMethodOptions"
                      :state="purchasePhoneErrors.sms_method ? false : null">
                    </b-form-select>
                  </b-form-group>
                  
                  <div class="mt-3">
                    <b-button variant="secondary" @click="selectedPhoneForPurchase = null" class="mr-2">Cancel Selection</b-button>
                    <b-button 
                      type="submit" 
                      variant="success" 
                      :disabled="isPurchasingPhone">
                      <b-spinner small v-if="isPurchasingPhone" class="mr-1"></b-spinner>
                      Purchase Phone Number
                    </b-button>
                  </div>
                </b-form>
              </b-card>
            </div>
          </div>
        </b-card>
        
        <!-- Phone Numbers Table -->
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>Phone Numbers</h5>
            <b-button 
              v-if="!showPhoneNumberWizard" 
              variant="primary" 
              @click="openPhoneNumberWizard">
              Add Phone Number
            </b-button>
          </div>
          <b-table
            :fields="phone_numbers_table.fields"
            :items="phone_numbers_table.items"
            :busy="phone_numbers_table.isBusy"
            light
            responsive
            show-empty
            empty-text="No Phone Numbers to Show"
            outlined>
            <template slot="table-busy">
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong> Loading...</strong>
              </div>
            </template>
            <template slot="cell(phone_number)" slot-scope="row">
              <strong>{{row.item.phone_number || 'N/A'}}</strong>
            </template>
            <template slot="cell(friendly_name)" slot-scope="row">
              {{row.item.friendly_name || 'N/A'}}
            </template>
            <template slot="cell(date_created)" slot-scope="row">
              {{formatDate(row.item.date_created)}}
            </template>
            <template slot="cell(capabilities)" slot-scope="row">
              <span v-if="row.item.capabilities">
                <b-badge v-if="row.item.capabilities.voice" variant="info" class="mr-1">Voice</b-badge>
                <b-badge v-if="row.item.capabilities.sms" variant="success" class="mr-1">SMS</b-badge>
                <b-badge v-if="row.item.capabilities.mms" variant="warning" class="mr-1">MMS</b-badge>
                <span v-if="!row.item.capabilities.voice && !row.item.capabilities.sms && !row.item.capabilities.mms">N/A</span>
              </span>
              <span v-else>N/A</span>
            </template>
            <template slot="cell(actions)" slot-scope="row">
              <b-button 
                variant="info" 
                size="sm" 
                @click="viewPhoneNumberDetails(row.item)"
                class="mr-2">
                View Details
              </b-button>
              <b-button 
                variant="primary" 
                size="sm" 
                @click="openEditPhoneNumberModal(row.item)">
                Edit
              </b-button>
            </template>
          </b-table>
        </b-card>
        
        <!-- Phone Number Details Modal -->
        <b-modal 
          id="phone-number-details-modal" 
          title="Phone Number Details" 
          size="lg"
          @hide="selectedPhoneNumber = null; showPhoneVoiceConfig = false; showPhoneSmsConfig = false; showPhoneCapabilities = false; showPhoneServices = false; phoneNumberServices = [];">
          <div v-if="selectedPhoneNumber">
            <b-card>
              <h6>Basic Information</h6>
              <p><strong>Phone Number:</strong> {{selectedPhoneNumber.phone_number || 'N/A'}}</p>
              <p><strong>Phone SID:</strong> {{selectedPhoneNumber.sid || 'N/A'}}</p>
              <p><strong>Friendly Name:</strong> {{selectedPhoneNumber.friendly_name || 'N/A'}}</p>
              <p><strong>Account SID:</strong> {{selectedPhoneNumber.account_sid || 'N/A'}}</p>
              <p><strong>Date Created:</strong> {{formatDate(selectedPhoneNumber.date_created)}}</p>
              <p><strong>Date Updated:</strong> {{formatDate(selectedPhoneNumber.date_updated)}}</p>
              <p v-if="selectedPhoneNumber.url"><strong>URL:</strong> {{selectedPhoneNumber.url}}</p>
              <p v-if="selectedPhoneNumber.api_version"><strong>API Version:</strong> {{selectedPhoneNumber.api_version}}</p>
              <p v-if="selectedPhoneNumber.address_requirements"><strong>Address Requirements:</strong> {{selectedPhoneNumber.address_requirements}}</p>
              <p v-if="selectedPhoneNumber.beta !== null"><strong>Beta:</strong> {{selectedPhoneNumber.beta ? 'Yes' : 'No'}}</p>
              <p v-if="selectedPhoneNumber.bundle_sid"><strong>Bundle SID:</strong> {{selectedPhoneNumber.bundle_sid}}</p>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showPhoneCapabilities = !showPhoneCapabilities" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Capabilities</h6>
                <span>{{ showPhoneCapabilities ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showPhoneCapabilities" class="mt-2">
                <p v-if="selectedPhoneNumber.capabilities">
                  <strong>Voice:</strong> {{selectedPhoneNumber.capabilities.voice ? 'Enabled' : 'Disabled'}}
                </p>
                <p v-if="selectedPhoneNumber.capabilities">
                  <strong>SMS:</strong> {{selectedPhoneNumber.capabilities.sms ? 'Enabled' : 'Disabled'}}
                </p>
                <p v-if="selectedPhoneNumber.capabilities">
                  <strong>MMS:</strong> {{selectedPhoneNumber.capabilities.mms ? 'Enabled' : 'Disabled'}}
                </p>
                <p v-if="selectedPhoneNumber.capabilities && selectedPhoneNumber.capabilities.fax">
                  <strong>Fax:</strong> {{selectedPhoneNumber.capabilities.fax ? 'Enabled' : 'Disabled'}}
                </p>
                <p v-if="!selectedPhoneNumber.capabilities">No capabilities information available</p>
              </b-collapse>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showPhoneVoiceConfig = !showPhoneVoiceConfig" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">Voice Configuration</h6>
                <span>{{ showPhoneVoiceConfig ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showPhoneVoiceConfig" class="mt-2">
                <p><strong>Voice URL:</strong> {{selectedPhoneNumber.voice_url || 'Not set'}}</p>
                <p><strong>Voice Method:</strong> {{selectedPhoneNumber.voice_method || 'Not set'}}</p>
                <p><strong>Voice Fallback URL:</strong> {{selectedPhoneNumber.voice_fallback_url || 'Not set'}}</p>
                <p><strong>Voice Fallback Method:</strong> {{selectedPhoneNumber.voice_fallback_method || 'Not set'}}</p>
                <p v-if="selectedPhoneNumber.voice_caller_id_lookup !== null">
                  <strong>Voice Caller ID Lookup:</strong> {{selectedPhoneNumber.voice_caller_id_lookup ? 'Enabled' : 'Disabled'}}
                </p>
              </b-collapse>
            </b-card>
            
            <b-card class="mt-3">
              <div 
                @click="showPhoneSmsConfig = !showPhoneSmsConfig" 
                style="cursor: pointer; user-select: none;"
                class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">SMS Configuration</h6>
                <span>{{ showPhoneSmsConfig ? '▼' : '▶' }}</span>
              </div>
              <b-collapse v-model="showPhoneSmsConfig" class="mt-2">
                <p><strong>SMS URL:</strong> {{selectedPhoneNumber.sms_url || 'Not set'}}</p>
                <p><strong>SMS Method:</strong> {{selectedPhoneNumber.sms_method || 'Not set'}}</p>
                <p><strong>SMS Fallback URL:</strong> {{selectedPhoneNumber.sms_fallback_url || 'Not set'}}</p>
                <p><strong>SMS Fallback Method:</strong> {{selectedPhoneNumber.sms_fallback_method || 'Not set'}}</p>
              </b-collapse>
            </b-card>
            
            <b-card class="mt-3" v-if="selectedPhoneNumber.status_callback">
              <h6>Status Callback</h6>
              <p><strong>Status Callback URL:</strong> {{selectedPhoneNumber.status_callback || 'Not set'}}</p>
              <p><strong>Status Callback Method:</strong> {{selectedPhoneNumber.status_callback_method || 'Not set'}}</p>
            </b-card>
          </div>
        </b-modal>
        
        <!-- Edit Phone Number Modal -->
        <b-modal 
          id="edit-phone-number-modal" 
          title="Edit Phone Number" 
          size="lg"
          @hide="resetEditPhoneNumberForm"
          @ok.prevent="updatePhoneNumber">
          <b-form @submit.stop.prevent="updatePhoneNumber">
            <b-form-group
              label="SMS Webhook URL"
              label-for="phone-sms-url"
              :invalid-feedback="phoneFormErrors.sms_url"
              :state="phoneFormErrors.sms_url ? false : null"
              description="The webhook URL where Twilio will send SMS messages">
              <b-input-group>
                <b-form-input
                  id="phone-sms-url"
                  v-model="phoneFormData.sms_url"
                  placeholder="Enter SMS webhook URL"
                  :state="phoneFormErrors.sms_url ? false : null">
                </b-form-input>
                <b-input-group-append>
                  <b-button 
                    variant="outline-secondary" 
                    @click="useWardWebhook"
                    title="Use ward webhook URL">
                    Use Ward Webhook
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            
            <b-form-group
              label="SMS Method"
              label-for="phone-sms-method"
              :invalid-feedback="phoneFormErrors.sms_method"
              :state="phoneFormErrors.sms_method ? false : null"
              description="HTTP method for SMS webhook requests">
              <b-form-select
                id="phone-sms-method"
                v-model="phoneFormData.sms_method"
                :options="smsMethodOptions"
                :state="phoneFormErrors.sms_method ? false : null">
              </b-form-select>
            </b-form-group>
          </b-form>
          <template slot="modal-footer">
            <b-button variant="secondary" @click="$bvModal.hide('edit-phone-number-modal')">Cancel</b-button>
            <b-button variant="primary" @click="updatePhoneNumber" :disabled="isUpdatingPhoneNumber">
              <b-spinner small v-if="isUpdatingPhoneNumber" class="mr-1"></b-spinner>
              Update Phone Number
            </b-button>
          </template>
        </b-modal>
      </b-tab>
      
      <!-- Brands Tab -->
      <b-tab title="Brands">
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5>Brand Registrations</h5>
            <b-button 
              variant="primary" 
              @click="openCreateBrandRegistrationModal">
              Create Brand Registration
            </b-button>
          </div>
          <b-table
            :fields="[
              { key: '_showDetails', label: '', thStyle: 'width: 40px' },
              { key: 'sid', label: 'Brand SID' },
              { key: 'brand_type', label: 'Brand Type' },
              { key: 'status', label: 'Status' },
              { key: 'tcr_id', label: 'TCR ID' },
              { key: 'brand_score', label: 'Brand Score' },
              { key: 'date_created', label: 'Created' },
              { key: 'actions', label: 'Actions' }
            ]"
            :items="brands_table.items"
            :busy="brands_table.isBusy"
            light
            responsive
            show-empty
            empty-text="No Brand Registrations to Show"
            outlined>
            <template slot="table-busy">
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong> Loading...</strong>
              </div>
            </template>
            <template slot="cell(_showDetails)" slot-scope="row">
              <b-button
                variant="link"
                size="sm"
                @click="toggleBrandRow(row.item, row.index)"
                :aria-expanded="isBrandRowExpanded(row.item.sid)"
                :aria-controls="`brand-campaigns-${row.index}`">
                <span v-if="isBrandRowExpanded(row.item.sid)">▼</span>
                <span v-else>▶</span>
              </b-button>
            </template>
            <template slot="cell(brand_type)" slot-scope="row">
              <span>{{row.item.brand_type || 'N/A'}}</span>
            </template>
            <template slot="cell(status)" slot-scope="row">
              <b-badge :variant="getBrandStatusVariant(row.item.status)">
                {{row.item.status || 'N/A'}}
              </b-badge>
            </template>
            <template slot="cell(brand_score)" slot-scope="row">
              <span>{{row.item.brand_score !== null && row.item.brand_score !== undefined ? row.item.brand_score : 'N/A'}}</span>
            </template>
            <template slot="cell(date_created)" slot-scope="row">
              {{formatDate(row.item.date_created)}}
            </template>
            <template slot="cell(actions)" slot-scope="row">
              <b-button 
                variant="primary" 
                size="sm" 
                @click="viewBrandDetails(row.item)"
                class="mr-1">
                View Details
              </b-button>
            </template>
            <template slot="row-details" slot-scope="row">
              <b-card>
                <h6 class="mb-3">Associated Campaigns</h6>
                <div v-if="isLoadingBrandCampaignsForRow(row.item.sid)" class="text-center my-2">
                  <b-spinner small></b-spinner>
                  <span class="ml-2">Loading campaigns...</span>
                </div>
                <div v-else-if="getBrandCampaignsForRow(row.item.sid).length > 0">
                  <b-table
                    :fields="[
                      { key: 'campaign_id', label: 'Campaign ID' },
                      { key: 'use_case', label: 'Use Case' },
                      { key: 'status', label: 'Status' },
                      { key: 'messaging_service_sid', label: 'Service SID' },
                      { key: 'date_created', label: 'Created' },
                      { key: 'actions', label: 'Actions' }
                    ]"
                    :items="getBrandCampaignsForRow(row.item.sid)"
                    light
                    responsive
                    small
                    outlined>
                    <template slot="cell(use_case)" slot-scope="campaignRow">
                      <span>{{campaignRow.item.use_case || 'Sole Proprietorship'}}</span>
                    </template>
                    <template slot="cell(status)" slot-scope="campaignRow">
                      <b-badge :variant="getCampaignStatusVariant(campaignRow.item.status)">
                        {{campaignRow.item.status || 'N/A'}}
                      </b-badge>
                    </template>
                    <template slot="cell(date_created)" slot-scope="campaignRow">
                      {{formatDate(campaignRow.item.date_created)}}
                    </template>
                    <template slot="cell(actions)" slot-scope="campaignRow">
                      <b-button 
                        variant="primary" 
                        size="sm" 
                        @click="viewCampaignDetails(campaignRow.item)"
                        class="mr-1">
                        View Details
                      </b-button>
                    </template>
                  </b-table>
                </div>
                <div v-else class="text-muted text-center my-2">
                  No campaigns found for this brand.
                </div>
              </b-card>
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>
    
    <!-- Create Usa2p Modal -->
    <b-modal 
      id="create-usa2p-modal" 
      v-model="showCreateUsa2pModal"
      title="Create Usa2p Resource (A2P 10DLC Campaign)" 
      size="lg"
      @hide="resetCreateUsa2pForm"
      @ok.prevent="createUsa2pResource">
      <b-form>
        <b-form-group
          label="Brand Registration"
          label-for="brand-select"
          :invalid-feedback="createUsa2pErrors.brand_registration_sid"
          :state="createUsa2pErrors.brand_registration_sid ? false : null">
          <b-form-select
            id="brand-select"
            v-model="createUsa2pForm.brand_registration_sid"
            :options="availableBrandsForUsa2p"
            :state="createUsa2pErrors.brand_registration_sid ? false : null"
            required>
            <template slot="first">
              <option :value="null">-- Select a brand --</option>
            </template>
          </b-form-select>
        </b-form-group>
        
        <b-form-group
          label="Messaging Service"
          label-for="service-select"
          :invalid-feedback="createUsa2pErrors.messaging_service_sid"
          :state="createUsa2pErrors.messaging_service_sid ? false : null">
          <b-form-select
            id="service-select"
            v-model="createUsa2pForm.messaging_service_sid"
            :options="availableServicesForUsa2p"
            :state="createUsa2pErrors.messaging_service_sid ? false : null"
            required>
            <template slot="first">
              <option :value="null">-- Select a messaging service --</option>
            </template>
          </b-form-select>
        </b-form-group>
        
        <b-form-group
          label="Use Case"
          label-for="usecase-input">
          <b-form-input
            id="usecase-input"
            v-model="createUsa2pForm.us_app_to_person_usecase"
            readonly
            disabled>
          </b-form-input>
          <small class="form-text text-muted">Currently only SOLE_PROPRIETOR is supported</small>
        </b-form-group>
        
        <b-form-group
          label="Description"
          label-for="description-input"
          :invalid-feedback="createUsa2pErrors.description"
          :state="createUsa2pErrors.description ? false : null"
          description="Minimum 40 characters">
          <b-form-textarea
            id="description-input"
            v-model="createUsa2pForm.description"
            :state="createUsa2pErrors.description ? false : null"
            rows="3"
            required>
          </b-form-textarea>
        </b-form-group>
        
        <b-form-group
          label="Sample Messages"
          label-for="message-samples"
          :invalid-feedback="createUsa2pErrors.message_samples"
          :state="createUsa2pErrors.message_samples ? false : null"
          description="At least 2 messages required, each with minimum 20 characters">
          <div v-for="(sample, index) in createUsa2pForm.message_samples" :key="index" class="mb-2">
            <b-form-input
              :id="`message-sample-${index}`"
              v-model="createUsa2pForm.message_samples[index]"
              :placeholder="`Sample message ${index + 1}`"
              :state="createUsa2pErrors.message_samples ? false : null">
            </b-form-input>
          </div>
          <b-button variant="outline-secondary" size="sm" @click="createUsa2pForm.message_samples.push('')" v-if="createUsa2pForm.message_samples.length < 5">
            Add Another Sample
          </b-button>
        </b-form-group>
      </b-form>
      <template slot="modal-footer">
        <b-button variant="secondary" @click="showCreateUsa2pModal = false; resetCreateUsa2pForm()">Cancel</b-button>
        <b-button variant="primary" @click="createUsa2pResource" :disabled="isCreatingUsa2p">
          <b-spinner small v-if="isCreatingUsa2p" class="mr-1"></b-spinner>
          Create
        </b-button>
      </template>
    </b-modal>
    
    <!-- Usa2p Details Modal -->
    <b-modal 
      id="usa2p-details-modal" 
      title="Usa2p Resource Details" 
      size="lg"
      @hide="selectedUsa2p = null">
      <div v-if="selectedUsa2p">
        <b-card>
          <h6>Basic Information</h6>
          <p><strong>Usa2p SID:</strong> {{selectedUsa2p.sid}}</p>
          <p><strong>Campaign ID:</strong> {{selectedUsa2p.campaign_id || 'N/A'}}</p>
          <p><strong>Use Case:</strong> {{selectedUsa2p.us_app_to_person_usecase || 'N/A'}}</p>
          <p><strong>Status:</strong> 
            <b-badge :variant="getUsa2pStatusVariant(selectedUsa2p.campaign_status)">
              {{selectedUsa2p.campaign_status || 'N/A'}}
            </b-badge>
          </p>
          <p><strong>Brand Registration SID:</strong> {{selectedUsa2p.brand_registration_sid || 'N/A'}}</p>
          <p><strong>Messaging Service SID:</strong> {{selectedUsa2p.messaging_service_sid || 'N/A'}}</p>
          <p><strong>Date Created:</strong> {{formatDate(selectedUsa2p.date_created)}}</p>
          <p><strong>Date Updated:</strong> {{formatDate(selectedUsa2p.date_updated)}}</p>
        </b-card>
        
        <b-card class="mt-3" v-if="selectedUsa2p.description">
          <h6>Description</h6>
          <p>{{selectedUsa2p.description}}</p>
        </b-card>
        
        <b-card class="mt-3" v-if="selectedUsa2p.message_samples && selectedUsa2p.message_samples.length > 0">
          <h6>Sample Messages</h6>
          <ul>
            <li v-for="(sample, index) in selectedUsa2p.message_samples" :key="index">{{sample}}</li>
          </ul>
        </b-card>
        
        <b-card class="mt-3" v-if="selectedUsa2p.errors && selectedUsa2p.errors.length > 0">
          <h6>Errors</h6>
          <ul>
            <li v-for="(error, index) in selectedUsa2p.errors" :key="index">{{error}}</li>
          </ul>
        </b-card>
      </div>
    </b-modal>
  </b-container>
</template>
<script>
import { WardApi } from "@/app/wards/wardApi";
import { StakeApi } from "@/app/stakes/stakeApi";
import { ROOT_URL } from "@/setup/constants";
import { userMixin } from "@/mixins/userMixin";
import handleError from "@/errorHandler";
export default {
  name: "WardTwilioSetUp",
  props: {
    wardId: {
      type: String,
      required: true
    }
  },
  mixins: [userMixin],
  data () {
    return {
      ward: null,
      twilioProfiles: null,
      wizardStep: 0,
      showWizard: false,
      formData: {
        friendly_name: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        status_callback: '',
        address_sid: null,
        street: '',
        city: '',
        region: '',
        postal_code: '',
        iso_country: 'US',
        customer_name: ''
      },
      addresses: [],
      isLoadingAddresses: false,
      errors: {},
      isProcessing: false,
      createdProfile: null,
      creationMessage: '',
      creationVariant: 'success',
      credentialError: null,
      countryOptions: [
        { value: 'US', text: 'United States' },
        { value: 'CA', text: 'Canada' },
        { value: 'MX', text: 'Mexico' },
        { value: 'GB', text: 'United Kingdom' },
        { value: 'AU', text: 'Australia' },
        { value: 'BR', text: 'Brazil' }
      ],
      profile_table: {
        id: "ward-twilio-profiles",
        isBusy: false,
        fields: [
          {
            key: "sid",
            label: "Profile ID"
          },
          {
            key: "status",
            label: "Twilio Status"
          },
          {
            key: "email",
            label: "Contact Email"
          },
          {
            key: "friendly_name",
            label: "Friendly Name"
          },
          {
            key: "actions",
            label: "Actions"
          }
        ],
        items: [],
        totalRows: 0
      },
      selectedProfile: null,
      showProfileDetails: false,
      profileEvaluations: [],
      isLoadingEvaluations: false,
      showEvaluations: false,
      services_table: {
        id: "ward-twilio-services",
        isBusy: false,
        fields: [
          {
            key: "sid",
            label: "Service SID"
          },
          {
            key: "friendly_name",
            label: "Friendly Name"
          },
          {
            key: "date_created",
            label: "Date Created"
          },
          {
            key: "actions",
            label: "Actions"
          }
        ],
        items: [],
        totalRows: 0
      },
      phone_numbers_table: {
        id: "ward-twilio-phone-numbers",
        isBusy: false,
        fields: [
          {
            key: "phone_number",
            label: "Phone Number"
          },
          {
            key: "friendly_name",
            label: "Friendly Name"
          },
          {
            key: "sid",
            label: "Phone SID"
          },
          {
            key: "capabilities",
            label: "Capabilities"
          },
          {
            key: "date_created",
            label: "Date Created"
          },
          {
            key: "actions",
            label: "Actions"
          }
        ],
        items: [],
        totalRows: 0
      },
      campaigns_table: {
        id: "ward-twilio-campaigns",
        isBusy: false,
        items: [],
        totalRows: 0
      },
      brands_table: {
        id: "ward-twilio-brands",
        isBusy: false,
        items: [],
        totalRows: 0
      },
      selectedBrand: null,
      showBrandCampaigns: false,
      brandCampaigns: [],
      isLoadingBrandCampaigns: false,
      expandedBrandRows: new Set(),
      brandCampaignsByRow: {},
      isLoadingBrandCampaignsByRow: {},
      selectedPhoneNumber: null,
      showPhoneVoiceConfig: false,
      showPhoneSmsConfig: false,
      showPhoneCapabilities: false,
      showPhoneServices: false,
      phoneNumberServices: [],
      isLoadingPhoneServices: false,
      selectedPhoneNumberForAddService: null,
      addServiceToPhoneForm: {
        service_sid: null
      },
      addServiceToPhoneErrors: {},
      isAddingServiceToPhone: false,
      showEditPhoneNumberModal: false,
      phoneFormData: {
        sms_url: '',
        sms_method: 'POST'
      },
      phoneFormErrors: {},
      isUpdatingPhoneNumber: false,
      smsMethodOptions: [
        { value: 'GET', text: 'GET' },
        { value: 'POST', text: 'POST' }
      ],
      showPhoneNumberWizard: false,
      phoneSearchStep: 0,
      phoneSearchForm: {
        country_code: 'US',
        area_code: null,
        contains: '',
        sms_enabled: true,
        limit: 20
      },
      phoneSearchResults: [],
      phoneSearchErrors: {},
      isSearchingNumbers: false,
      selectedPhoneForPurchase: null,
      purchasePhoneForm: {
        sms_url: '',
        sms_method: 'POST'
      },
      purchasePhoneErrors: {},
      isPurchasingPhone: false,
      countryCodeOptions: [
        { value: 'US', text: 'United States' },
        { value: 'CA', text: 'Canada' },
        { value: 'GB', text: 'United Kingdom' },
        { value: 'AU', text: 'Australia' },
        { value: 'MX', text: 'Mexico' },
        { value: 'BR', text: 'Brazil' },
        { value: 'FR', text: 'France' },
        { value: 'DE', text: 'Germany' },
        { value: 'ES', text: 'Spain' },
        { value: 'IT', text: 'Italy' }
      ],
      searchLimitOptions: [
        { value: 20, text: '20 results' },
        { value: 50, text: '50 results' },
        { value: 100, text: '100 results' }
      ],
      availableNumbersTableFields: [
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'location', label: 'Location' },
        { key: 'capabilities', label: 'Capabilities' },
        { key: 'address_requirements', label: 'Address Required' },
        { key: 'actions', label: 'Actions' }
      ],
      selectedService: null,
      showCreateServiceModal: false,
      showConfiguration: false,
      showFeatures: false,
      showLinks: false,
      showServicePhoneNumbers: false,
      servicePhoneNumbers: [],
      isLoadingServicePhoneNumbers: false,
      selectedServiceForAddPhone: null,
      addPhoneToServiceForm: {
        phone_number_sid: null
      },
      addPhoneToServiceErrors: {},
      isAddingPhoneToService: false,
      isRemovingPhoneFromService: false,
      servicePhoneNumbersTableFields: [
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'country_code', label: 'Country' },
        { key: 'capabilities', label: 'Capabilities' },
        { key: 'actions', label: 'Actions' }
      ],
      serviceFormData: {
        friendly_name: ''
      },
      serviceFormErrors: {},
      isCreatingService: false,
      showSetupWarning: true,
      showServiceCampaigns: false,
      serviceCampaigns: [],
      isLoadingServiceCampaigns: false,
      showServiceUsa2p: false,
      serviceUsa2pResources: [],
      isLoadingServiceUsa2p: false,
      showCreateUsa2pModal: false,
      createUsa2pForm: {
        brand_registration_sid: null,
        messaging_service_sid: null,
        description: '',
        message_samples: ['', ''],
        us_app_to_person_usecase: 'SOLE_PROPRIETOR'
      },
      createUsa2pErrors: {},
      isCreatingUsa2p: false,
      selectedUsa2p: null,
      isDeletingUsa2p: false,
      showCreateCampaignModal: false,
      createCampaignForm: {
        messaging_service_sid: null,
        description: 'Starter US A2P Campaign for testing or small-scale sending.',
        sample_messages: [
          'Twilio Starter Campaign. Messages may contain links https://www.twilio.com/messaging as customers build on Twilio.',
          'Twilio Starter campaign, Messages may contain callback numbers like +1400000000'
        ],
        opt_in_process: ''
      },
      createCampaignErrors: {},
      isCreatingCampaign: false,
      selectedCampaign: null,
      showCampaignDetailsModal: false,
      isDeletingCampaign: false,
      isResubmittingCampaign: false,
      isDeletingProfile: false,
      showCreateBrandRegistrationModal: false,
      createBrandRegistrationForm: {
        customer_profile_bundle_sid: null,
        a2p_profile_bundle_sid: null,
        brand_type: null,
        mock: false,
        skip_automatic_sec_vet: false
      },
      createBrandRegistrationErrors: {},
      isCreatingBrandRegistration: false
    }
  },
  computed: {
    inboundRequestUrl() {
      return `${ROOT_URL}/hook/${this.wardId}`;
    },
    wardWebhookUrl() {
      return `${ROOT_URL}/hook/${this.wardId}`;
    },
    hasApprovedProfile() {
      if (!this.profile_table || !this.profile_table.items || !Array.isArray(this.profile_table.items)) {
        return false;
      }
      // Check if any profile has status "twilio-approved"
      return this.profile_table.items.some(profile => 
        profile && profile.status && profile.status.toLowerCase() === 'twilio-approved'
      );
    },
    hasMessagingService() {
      if (!this.services_table || !this.services_table.items) {
        return false;
      }
      if (!Array.isArray(this.services_table.items)) {
        return false;
      }
      const hasService = this.services_table.items.length > 0;
      return hasService;
    },
    hasPhoneNumber() {
      if (!this.phone_numbers_table || !this.phone_numbers_table.items || !Array.isArray(this.phone_numbers_table.items)) {
        return false;
      }
      return this.phone_numbers_table.items.length > 0;
    },
    availablePhoneNumbersForService() {
      if (!this.selectedServiceForAddPhone || !this.phone_numbers_table.items) {
        return [];
      }
      
      // Get phone number SIDs already in the service
      const servicePhoneSids = this.servicePhoneNumbers.map(p => p.sid);
      
      // Filter out phone numbers already in the service
      return this.phone_numbers_table.items
        .filter(phone => !servicePhoneSids.includes(phone.sid))
        .map(phone => ({
          value: phone.sid,
          text: `${phone.phone_number || phone.sid}${phone.friendly_name ? ' - ' + phone.friendly_name : ''}`
        }));
    },
    availableServicesForPhone() {
      if (!this.selectedPhoneNumberForAddService || !this.services_table.items) {
        return [];
      }
      
      // Get service SIDs this phone is already in
      const phoneServiceSids = this.phoneNumberServices.map(s => s.sid);
      
      // Filter out services the phone is already in
      return this.services_table.items
        .filter(service => !phoneServiceSids.includes(service.sid))
        .map(service => ({
          value: service.sid,
          text: `${service.friendly_name || service.sid}`
        }));
    },
    servicesWithPhoneNumbers() {
      if (!this.services_table.items || !this.phone_numbers_table.items) {
        return [];
      }
      
      // This is a simplified check - in a real scenario, you'd want to check each service
      // For now, we'll show all services and let the backend validate
      return this.services_table.items.map(service => ({
        value: service.sid,
        text: `${service.friendly_name || service.sid}`
      }));
    },
    showSetupWarningComputed() {
      const hasProfile = this.hasApprovedProfile;
      const hasService = this.hasMessagingService;
      const hasPhone = this.hasPhoneNumber;
      // Show warning if any requirement is missing
      const shouldShow = !hasProfile || !hasService || !hasPhone;
      return shouldShow;
    },
    availableBrandsForUsa2p() {
      if (!this.brands_table || !this.brands_table.items) {
        return [];
      }
      return this.brands_table.items
        .filter(brand => brand.status === 'APPROVED')
        .map(brand => ({
          value: brand.sid,
          text: `${brand.brand_type || 'Brand'} - ${brand.sid}`
        }));
    },
    availableServicesForUsa2p() {
      if (!this.services_table || !this.services_table.items) {
        return [];
      }
      return this.services_table.items.map(service => ({
        value: service.sid,
        text: `${service.friendly_name || service.sid}`
      }));
    },
    availableCustomerProfileBundles() {
      // For now, return empty array - bundle SIDs need to be entered manually
      // In the future, we could fetch bundles from profiles or existing brand registrations
      // Users can reference bundle SIDs from existing brand registrations or Twilio console
      return [];
    },
    addressOptions() {
      if (!this.addresses || !Array.isArray(this.addresses)) {
        return [];
      }
      return this.addresses.map(addr => ({
        value: addr.sid,
        text: `${addr.customer_name || 'Address'} - ${addr.street || ''}, ${addr.city || ''}, ${addr.region || ''} (${addr.sid})`
      }));
    }
  },
    async mounted() {
    if(this.wardId!==null) {
      this.ward = await this.getWard();
      await this.getTwilioProfiles();
      await this.getTwilioServices();
      await this.getTwilioPhoneNumbers();
      await this.getTwilioBrandRegistrations();
    }
  },
  methods: {
    async getWard() {
      try{
        const { data } = await WardApi.getWardDetail(this.wardId);
        return data.ward;
      } catch(e) {
        handleError(e, "Error trying to get Ward Information");
      }
    },
    async getTwilioProfiles() {
      try {
        this.profile_table.isBusy = true;
        this.credentialError = null;
        const { data } = await WardApi.getWardTwilioProfiles(this.wardId);
        this.profile_table.items = data.profiles || [];
        this.profile_table.totalRows = data.count || 0;
      } catch(e) {
        // Always ensure items is an array, even on error
        this.profile_table.items = [];
        this.profile_table.totalRows = 0;
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to access Twilio profiles. This usually means the Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get Twilio Profiles");
        }
      } finally {
        this.profile_table.isBusy = false;
        // Reset warning flag when data changes so it can reappear if conditions are still unmet
        this.$nextTick(() => {
          if (this.showSetupWarningComputed) {
            this.showSetupWarning = true;
          }
        });
        // Force Vue to update
        this.$forceUpdate();
      }
    },
    validateStep(step) {
      this.errors = {};
      let isValid = true;
      
      if (step === 0) {
        // Validate Step 1: Profile Information
        if (!this.formData.friendly_name) {
          this.errors.friendly_name = 'Friendly name is required';
          isValid = false;
        }
        if (!this.formData.first_name) {
          this.errors.first_name = 'First name is required';
          isValid = false;
        }
        if (!this.formData.last_name) {
          this.errors.last_name = 'Last name is required';
          isValid = false;
        }
        if (!this.formData.phone_number) {
          this.errors.phone_number = 'Phone number is required';
          isValid = false;
        }
        if (!this.formData.email) {
          this.errors.email = 'Email is required';
          isValid = false;
        } else if (!this.isValidEmail(this.formData.email)) {
          this.errors.email = 'Please enter a valid email address';
          isValid = false;
        }
      } else if (step === 1) {
        // Validate Step 2: Address Information - either address_sid OR all address fields required
        if (!this.formData.address_sid) {
          // If no address_sid, all address fields are required
          if (!this.formData.street) {
            this.errors.street = 'Street address is required when not using an existing address';
            isValid = false;
          }
          if (!this.formData.city) {
            this.errors.city = 'City is required when not using an existing address';
            isValid = false;
          }
          if (!this.formData.region) {
            this.errors.region = 'Region/State is required when not using an existing address';
            isValid = false;
          }
          if (!this.formData.postal_code) {
            this.errors.postal_code = 'Postal code is required when not using an existing address';
            isValid = false;
          }
          if (!this.formData.iso_country) {
            this.errors.iso_country = 'Country is required when not using an existing address';
            isValid = false;
          }
        }
      }
      
      return isValid;
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    async goToStep(step) {
      // Load addresses when entering step 2 (address step)
      if (step === 1 && this.addresses.length === 0 && !this.isLoadingAddresses) {
        await this.loadAddresses();
      }
      if (step < this.wizardStep) {
        // Going back, no validation needed
        this.wizardStep = step;
      } else if (step > this.wizardStep) {
        // Going forward, validate current step
        if (this.validateStep(this.wizardStep)) {
          this.wizardStep = step;
        }
      } else {
        this.wizardStep = step;
      }
    },
    async nextStep() {
      if (this.validateStep(this.wizardStep)) {
        // Load addresses when moving to step 2
        if (this.wizardStep === 0 && this.addresses.length === 0 && !this.isLoadingAddresses) {
          await this.loadAddresses();
        }
        if (this.wizardStep < 2) {
          this.wizardStep++;
        }
      }
    },
    prevStep() {
      if (this.wizardStep > 0) {
        this.wizardStep--;
      }
    },
    async createProfile() {
      this.isProcessing = true;
      this.creationMessage = '';
      this.errors = {};
      this.credentialError = null;
      
      try {
        // Build profile data - include address_sid if provided, otherwise include address fields
        const profileData = {
          friendly_name: this.formData.friendly_name,
          first_name: this.formData.first_name,
          last_name: this.formData.last_name,
          phone_number: this.formData.phone_number,
          email: this.formData.email,
          status_callback: this.formData.status_callback || undefined,
        };
        
        // Add address information - either address_sid or address fields
        if (this.formData.address_sid) {
          profileData.address_sid = this.formData.address_sid;
        } else {
          profileData.street = this.formData.street;
          profileData.city = this.formData.city;
          profileData.region = this.formData.region;
          profileData.postal_code = this.formData.postal_code;
          profileData.iso_country = this.formData.iso_country;
          profileData.customer_name = this.formData.customer_name || undefined;
        }
        
        const response = await WardApi.createWardTwilioStarterProfile(this.wardId, profileData);
        this.createdProfile = response.data;
        
        if (this.createdProfile.errors && Object.keys(this.createdProfile.errors).length > 0) {
          this.creationVariant = 'warning';
          this.creationMessage = `Profile created with some errors. Completed steps: ${this.createdProfile.completed_steps.join(', ')}`;
        } else {
          this.creationVariant = 'success';
          this.creationMessage = `Profile created successfully! Profile SID: ${this.createdProfile.profile_sid}`;
        }
        
        // Refresh profile list
        await this.getTwilioProfiles();
      } catch(e) {
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to create Twilio profile. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid. Please configure them in the ward settings first.';
          this.creationVariant = 'danger';
          this.creationMessage = 'Cannot create profile: Twilio credentials are missing or invalid.';
        } else {
          this.creationVariant = 'danger';
          if (e.response && e.response.data && e.response.data.detail) {
            this.creationMessage = `Error: ${e.response.data.detail}`;
          } else {
            this.creationMessage = 'Error creating profile. Please try again.';
          }
          handleError(e, "Error creating Twilio starter profile");
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async runEvaluation() {
      if (!this.createdProfile || !this.createdProfile.profile_sid) {
        handleError(new Error('No profile SID available'), 'Cannot run evaluation');
        return;
      }
      
      this.isProcessing = true;
      this.credentialError = null;
      try {
        await WardApi.runWardTwilioEvaluation(this.wardId, this.createdProfile.profile_sid);
        this.creationVariant = 'success';
        this.creationMessage = 'Evaluation submitted successfully!';
        await this.getTwilioProfiles();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to run evaluation. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        }
        this.creationVariant = 'danger';
        this.creationMessage = 'Error running evaluation. Please try again.';
        handleError(e, "Error running evaluation");
      } finally {
        this.isProcessing = false;
      }
    },
    async submitForReview() {
      if (!this.createdProfile || !this.createdProfile.profile_sid) {
        handleError(new Error('No profile SID available'), 'Cannot submit for review');
        return;
      }
      
      this.isProcessing = true;
      this.credentialError = null;
      try {
        await WardApi.submitWardTwilioProfile(this.wardId, this.createdProfile.profile_sid);
        this.creationVariant = 'success';
        this.creationMessage = 'Profile submitted for review successfully!';
        await this.getTwilioProfiles();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to submit profile. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        }
        this.creationVariant = 'danger';
        this.creationMessage = 'Error submitting profile. Please try again.';
        handleError(e, "Error submitting profile");
      } finally {
        this.isProcessing = false;
      }
    },
    async loadAddresses() {
      this.isLoadingAddresses = true;
      try {
        const { data } = await WardApi.getWardTwilioAddresses(this.wardId);
        this.addresses = data.addresses || [];
      } catch(e) {
        this.addresses = [];
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          // Credential error - already handled globally
        } else {
          handleError(e, "Error loading addresses");
        }
      } finally {
        this.isLoadingAddresses = false;
      }
    },
    onAddressSidChange(value) {
      // When an address is selected from dropdown, update formData
      this.formData.address_sid = value;
    },
    onAddressSidInput(value) {
      // When manually entering address SID, update formData
      this.formData.address_sid = value || null;
    },
    hideWizard() {
      this.showWizard = false;
      this.wizardStep = 0;
      this.formData = {
        friendly_name: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        status_callback: '',
        address_sid: null,
        street: '',
        city: '',
        region: '',
        postal_code: '',
        iso_country: 'US',
        customer_name: ''
      };
      this.errors = {};
      this.credentialError = null;
    },
    resetWizard() {
      this.wizardStep = 0;
      this.formData = {
        friendly_name: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        status_callback: '',
        address_sid: null,
        street: '',
        city: '',
        region: '',
        postal_code: '',
        iso_country: 'US',
        customer_name: ''
      };
      this.errors = {};
      this.createdProfile = null;
      this.creationMessage = '';
      this.isProcessing = false;
      this.credentialError = null;
      this.showWizard = true; // Keep wizard visible when creating another profile
    },
    async getTwilioServices() {
      try {
        this.services_table.isBusy = true;
        this.credentialError = null;
        const { data } = await WardApi.getWardTwilioServices(this.wardId);
        this.services_table.items = data.services || [];
        this.services_table.totalRows = data.count || 0;
      } catch(e) {
        // Always ensure items is an array, even on error
        this.services_table.items = [];
        this.services_table.totalRows = 0;
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to access Twilio messaging services. This usually means the Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get Twilio Messaging Services");
        }
      } finally {
        this.services_table.isBusy = false;
        // Reset warning flag when data changes so it can reappear if conditions are still unmet
        this.$nextTick(() => {
          if (this.showSetupWarningComputed) {
            this.showSetupWarning = true;
          }
          // Force Vue to update
          this.$forceUpdate();
        });
      }
    },
    async getTwilioPhoneNumbers() {
      try {
        this.phone_numbers_table.isBusy = true;
        this.credentialError = null;
        const { data } = await WardApi.getWardTwilioPhoneNumbers(this.wardId);
        this.phone_numbers_table.items = data.phone_numbers || [];
        this.phone_numbers_table.totalRows = data.count || 0;
      } catch(e) {
        // Always ensure items is an array, even on error
        this.phone_numbers_table.items = [];
        this.phone_numbers_table.totalRows = 0;
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to access Twilio phone numbers. This usually means the Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get Twilio Phone Numbers");
        }
      } finally {
        this.phone_numbers_table.isBusy = false;
        // Reset warning flag when data changes so it can reappear if conditions are still unmet
        this.$nextTick(() => {
          if (this.showSetupWarningComputed) {
            this.showSetupWarning = true;
          }
          // Force Vue to update
          this.$forceUpdate();
        });
      }
    },
    async getTwilioBrandRegistrations() {
      try {
        this.brands_table.isBusy = true;
        this.credentialError = null;
        const { data } = await WardApi.getWardTwilioBrandRegistrations(this.wardId);
        // Initialize _showDetails property for each brand
        const brands = (data.brand_registrations || []).map(brand => ({
          ...brand,
          _showDetails: this.expandedBrandRows.has(brand.sid)
        }));
        this.brands_table.items = brands;
        this.brands_table.totalRows = data.count || 0;
      } catch(e) {
        // Always ensure items is an array, even on error
        this.brands_table.items = [];
        this.brands_table.totalRows = 0;
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to access Twilio brand registrations. This usually means the Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get Twilio Brand Registrations");
        }
      } finally {
        this.brands_table.isBusy = false;
      }
    },
    async getTwilioCampaigns() {
      try {
        this.campaigns_table.isBusy = true;
        this.credentialError = null;
        const { data } = await WardApi.getWardTwilioCampaigns(this.wardId);
        this.campaigns_table.items = data.campaigns || [];
        this.campaigns_table.totalRows = data.count || 0;
      } catch(e) {
        // Always ensure items is an array, even on error
        this.campaigns_table.items = [];
        this.campaigns_table.totalRows = 0;
        // Check for 401/403 errors indicating missing or invalid credentials
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to access Twilio campaigns. This usually means the Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get Twilio Campaigns");
        }
      } finally {
        this.campaigns_table.isBusy = false;
      }
    },
    async viewServiceDetails(service) {
      try {
        this.services_table.isBusy = true;
        // Reset collapse states when opening modal
        this.showConfiguration = false;
        this.showFeatures = false;
        this.showLinks = false;
        this.showServicePhoneNumbers = false;
        this.servicePhoneNumbers = [];
        this.showServiceUsa2p = false;
        this.serviceUsa2pResources = [];
        const { data } = await WardApi.getWardTwilioServiceDetail(this.wardId, service.sid);
        this.selectedService = data;
        this.$bvModal.show('service-details-modal');
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to fetch service details. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get service details");
        }
      } finally {
        this.services_table.isBusy = false;
      }
    },
    async loadServicePhoneNumbers() {
      if (!this.selectedService || !this.selectedService.sid) {
        return;
      }
      
      this.isLoadingServicePhoneNumbers = true;
      try {
        const { data } = await WardApi.getServicePhoneNumbers(this.wardId, this.selectedService.sid);
        this.servicePhoneNumbers = data.phone_numbers || [];
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to fetch service phone numbers. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to get service phone numbers");
        }
        this.servicePhoneNumbers = [];
      } finally {
        this.isLoadingServicePhoneNumbers = false;
      }
    },
    openAddPhoneToServiceModal(service) {
      this.selectedServiceForAddPhone = service;
      this.addPhoneToServiceForm.phone_number_sid = null;
      this.addPhoneToServiceErrors = {};
      this.$bvModal.show('add-phone-to-service-modal');
    },
    resetAddPhoneToServiceForm() {
      this.selectedServiceForAddPhone = null;
      this.addPhoneToServiceForm.phone_number_sid = null;
      this.addPhoneToServiceErrors = {};
    },
    async addPhoneToService() {
      this.addPhoneToServiceErrors = {};
      
      if (!this.addPhoneToServiceForm.phone_number_sid) {
        this.addPhoneToServiceErrors.phone_number_sid = 'Please select a phone number';
        return;
      }
      
      if (!this.selectedServiceForAddPhone || !this.selectedServiceForAddPhone.sid) {
        handleError(new Error('No service selected'), 'Cannot add phone number');
        return;
      }
      
      // Check if phone number is already in the service
      // First, ensure we have the current list of phone numbers for this service
      if (this.selectedService && this.selectedService.sid === this.selectedServiceForAddPhone.sid && this.servicePhoneNumbers.length > 0) {
        const isAlreadyInService = this.servicePhoneNumbers.some(p => p.sid === this.addPhoneToServiceForm.phone_number_sid);
        if (isAlreadyInService) {
          this.$toast.error('This phone number is already in this service.');
          return;
        }
      } else {
        // If we don't have the list, fetch it first to check
        try {
          const { data } = await WardApi.getServicePhoneNumbers(this.wardId, this.selectedServiceForAddPhone.sid);
          const existingPhoneNumbers = data.phone_numbers || [];
          const isAlreadyInService = existingPhoneNumbers.some(p => p.sid === this.addPhoneToServiceForm.phone_number_sid);
          if (isAlreadyInService) {
            this.$toast.error('This phone number is already in this service.');
            return;
          }
        } catch(e) {
          // If we can't fetch the list, continue with the API call and let the backend handle it
          // This is a safety check, not a blocker
        }
      }
      
      this.isAddingPhoneToService = true;
      this.credentialError = null;
      
      try {
        await WardApi.addPhoneNumberToService(
          this.wardId,
          this.selectedServiceForAddPhone.sid,
          this.addPhoneToServiceForm.phone_number_sid
        );
        
        this.$toast.success('Phone number added to service successfully');
        
        // Refresh service phone numbers if modal is open
        if (this.selectedService && this.selectedService.sid === this.selectedServiceForAddPhone.sid && this.showServicePhoneNumbers) {
          await this.loadServicePhoneNumbers();
        }
        
        // Close modal
        this.$bvModal.hide('add-phone-to-service-modal');
        this.resetAddPhoneToServiceForm();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to add phone number to service. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else if (e.response && e.response.status === 400) {
          this.$toast.error(e.response.data.detail || 'This phone number is already in this service.');
        } else {
          handleError(e, "Error trying to add phone number to service");
        }
      } finally {
        this.isAddingPhoneToService = false;
      }
    },
    async removePhoneFromService(phoneNumberSid) {
      if (!this.selectedService || !this.selectedService.sid) {
        return;
      }
      
      if (!confirm('Are you sure you want to remove this phone number from the service?')) {
        return;
      }
      
      this.isRemovingPhoneFromService = true;
      this.credentialError = null;
      
      try {
        await WardApi.removePhoneNumberFromService(
          this.wardId,
          this.selectedService.sid,
          phoneNumberSid
        );
        
        this.$toast.success('Phone number removed from service successfully');
        
        // Refresh service phone numbers
        await this.loadServicePhoneNumbers();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to remove phone number from service. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to remove phone number from service");
        }
      } finally {
        this.isRemovingPhoneFromService = false;
      }
    },
    isProfileDraft(profile) {
      // Check if profile status is 'draft' (case-insensitive)
      if (!profile || !profile.status) {
        return false;
      }
      return profile.status.toLowerCase() === 'draft';
    },
    async confirmDeleteProfile(profile) {
      if (!profile || !profile.sid) {
        return;
      }
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the profile "${profile.friendly_name || profile.sid}"? This action cannot be undone.`,
        {
          title: 'Delete Profile',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true
        }
      );
      
      if (confirmed) {
        await this.deleteProfile(profile);
      }
    },
    async deleteProfile(profile) {
      if (!profile || !profile.sid) {
        return;
      }
      
      this.isDeletingProfile = true;
      try {
        await WardApi.deleteWardTwilioProfile(this.wardId, profile.sid);
        
        this.$bvToast.toast('Profile deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Refresh profiles list
        await this.getTwilioProfiles();
        
        // Close profile details modal if it's open for this profile
        if (this.selectedProfile && this.selectedProfile.sid === profile.sid) {
          this.$bvModal.hide('profile-details-modal');
          this.selectedProfile = null;
        }
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to delete profile. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else if (e.response && e.response.status === 400) {
          this.$bvToast.toast(e.response.data.detail || 'Cannot delete profile. Only profiles with status "draft" can be deleted.', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        } else {
          handleError(e, "Error deleting profile");
        }
      } finally {
        this.isDeletingProfile = false;
      }
    },
    viewProfileDetails(profile) {
      // Reset collapse state when opening modal
      this.showProfileDetails = false;
      this.showEvaluations = false;
      this.profileEvaluations = [];
      
      // Set the selected profile
      this.selectedProfile = profile;
      
      // Show the modal
      this.$bvModal.show('profile-details-modal');
    },
    async toggleEvaluations() {
      this.showEvaluations = !this.showEvaluations;
      
      // Load evaluations when expanding for the first time
      if (this.showEvaluations && this.profileEvaluations.length === 0 && !this.isLoadingEvaluations && this.selectedProfile && this.selectedProfile.sid) {
        await this.loadProfileEvaluations();
      }
    },
    async loadProfileEvaluations() {
      if (!this.selectedProfile || !this.selectedProfile.sid) {
        return;
      }
      
      this.isLoadingEvaluations = true;
      try {
        const { data } = await WardApi.getWardTwilioProfileEvaluations(this.wardId, this.selectedProfile.sid);
        this.profileEvaluations = data.evaluations || [];
      } catch(e) {
        this.profileEvaluations = [];
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          // Credential error - already handled globally
        } else {
          handleError(e, "Error loading evaluations");
        }
      } finally {
        this.isLoadingEvaluations = false;
      }
    },
    getEvaluationStatusVariant(status) {
      if (!status) return 'secondary';
      const statusLower = status.toLowerCase();
      if (statusLower === 'compliant' || statusLower === 'approved' || statusLower === 'passed') {
        return 'success';
      } else if (statusLower === 'noncompliant' || statusLower === 'rejected' || statusLower === 'failed') {
        return 'danger';
      } else if (statusLower === 'pending' || statusLower === 'in-progress') {
        return 'warning';
      }
      return 'info';
    },
    getProfileStatusVariant(status) {
      if (!status) return 'secondary';
      const statusLower = status.toLowerCase();
      if (statusLower === 'twilio-approved' || statusLower === 'approved') {
        return 'success';
      } else if (statusLower === 'pending' || statusLower === 'pending-review') {
        return 'warning';
      } else if (statusLower === 'rejected' || statusLower === 'failed') {
        return 'danger';
      }
      return 'info';
    },
    viewPhoneNumberDetails(phoneNumber) {
      // Reset collapse states when opening modal
      this.showPhoneVoiceConfig = false;
      this.showPhoneSmsConfig = false;
      this.showPhoneCapabilities = false;
      this.showPhoneServices = false;
      this.phoneNumberServices = [];
      
      // Set the selected phone number
      this.selectedPhoneNumber = phoneNumber;
      
      // Show the modal
      this.$bvModal.show('phone-number-details-modal');
    },
    async loadPhoneNumberServices() {
      if (!this.selectedPhoneNumber || !this.selectedPhoneNumber.sid) {
        return;
      }
      
      this.isLoadingPhoneServices = true;
      try {
        // Get all services and check which ones contain this phone number
        const services = this.services_table.items || [];
        const phoneNumberSid = this.selectedPhoneNumber.sid;
        
        // Fetch phone numbers for each service to check if this phone is in it
        const servicesWithPhone = [];
        for (const service of services) {
          try {
            const { data } = await WardApi.getServicePhoneNumbers(this.wardId, service.sid);
            const phoneNumbers = data.phone_numbers || [];
            if (phoneNumbers.some(p => p.sid === phoneNumberSid)) {
              servicesWithPhone.push(service);
            }
          } catch(e) {
            // Skip services that fail to load
            continue;
          }
        }
        
        this.phoneNumberServices = servicesWithPhone;
      } catch(e) {
        handleError(e, "Error trying to get phone number services");
        this.phoneNumberServices = [];
      } finally {
        this.isLoadingPhoneServices = false;
      }
    },
    async openAddServiceToPhoneModal() {
      this.selectedPhoneNumberForAddService = this.selectedPhoneNumber;
      this.addServiceToPhoneForm.service_sid = null;
      this.addServiceToPhoneErrors = {};
      
      // Load current services for this phone number to ensure accurate filtering
      // This ensures the computed property has up-to-date data
      if (this.selectedPhoneNumber && this.showPhoneServices && this.phoneNumberServices.length > 0) {
        // If we already have the list loaded, use it
        // The computed property will use this.phoneNumberServices
      } else {
        // Try to load it in the background (non-blocking)
        // We'll check all services to see which ones contain this phone
        try {
          const services = this.services_table.items || [];
          const phoneNumberSid = this.selectedPhoneNumber.sid;
          const servicesWithPhone = [];
          
          for (const service of services) {
            try {
              const { data } = await WardApi.getServicePhoneNumbers(this.wardId, service.sid);
              const phoneNumbers = data.phone_numbers || [];
              if (phoneNumbers.some(p => p.sid === phoneNumberSid)) {
                servicesWithPhone.push(service);
              }
            } catch(e) {
              // Skip services that fail to load
              continue;
            }
          }
          
          this.phoneNumberServices = servicesWithPhone;
        } catch(e) {
          // If it fails, the computed property will still work with empty array
          this.phoneNumberServices = [];
        }
      }
      
      this.$bvModal.show('add-service-to-phone-modal');
    },
    resetAddServiceToPhoneForm() {
      this.selectedPhoneNumberForAddService = null;
      this.addServiceToPhoneForm.service_sid = null;
      this.addServiceToPhoneErrors = {};
    },
    async addServiceToPhone() {
      this.addServiceToPhoneErrors = {};
      
      if (!this.addServiceToPhoneForm.service_sid) {
        this.addServiceToPhoneErrors.service_sid = 'Please select a service';
        return;
      }
      
      if (!this.selectedPhoneNumberForAddService || !this.selectedPhoneNumberForAddService.sid) {
        handleError(new Error('No phone number selected'), 'Cannot add to service');
        return;
      }
      
      // Check if phone number is already in the service
      // First, check if we already have the list of services for this phone
      if (this.selectedPhoneNumber && this.selectedPhoneNumber.sid === this.selectedPhoneNumberForAddService.sid && this.phoneNumberServices.length > 0) {
        const isAlreadyInService = this.phoneNumberServices.some(s => s.sid === this.addServiceToPhoneForm.service_sid);
        if (isAlreadyInService) {
          this.$toast.error('This phone number is already in this service.');
          return;
        }
      } else {
        // If we don't have the list, fetch phone numbers for the service to check
        try {
          const { data } = await WardApi.getServicePhoneNumbers(this.wardId, this.addServiceToPhoneForm.service_sid);
          const servicePhoneNumbers = data.phone_numbers || [];
          const isAlreadyInService = servicePhoneNumbers.some(p => p.sid === this.selectedPhoneNumberForAddService.sid);
          if (isAlreadyInService) {
            this.$toast.error('This phone number is already in this service.');
            return;
          }
        } catch(e) {
          // If we can't fetch the list, continue with the API call and let the backend handle it
          // This is a safety check, not a blocker
        }
      }
      
      this.isAddingServiceToPhone = true;
      this.credentialError = null;
      
      try {
        await WardApi.addPhoneNumberToService(
          this.wardId,
          this.addServiceToPhoneForm.service_sid,
          this.selectedPhoneNumberForAddService.sid
        );
        
        this.$toast.success('Phone number added to service successfully');
        
        // Refresh phone number services if modal is open
        if (this.selectedPhoneNumber && this.selectedPhoneNumber.sid === this.selectedPhoneNumberForAddService.sid && this.showPhoneServices) {
          await this.loadPhoneNumberServices();
        }
        
        // Close modal
        this.$bvModal.hide('add-service-to-phone-modal');
        this.resetAddServiceToPhoneForm();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to add phone number to service. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else if (e.response && e.response.status === 400) {
          this.$toast.error(e.response.data.detail || 'This phone number is already in this service.');
        } else {
          handleError(e, "Error trying to add phone number to service");
        }
      } finally {
        this.isAddingServiceToPhone = false;
      }
    },
    async removePhoneFromServiceInPhoneModal(serviceSid) {
      if (!this.selectedPhoneNumber || !this.selectedPhoneNumber.sid) {
        return;
      }
      
      if (!confirm('Are you sure you want to remove this phone number from the service?')) {
        return;
      }
      
      this.isRemovingPhoneFromService = true;
      this.credentialError = null;
      
      try {
        await WardApi.removePhoneNumberFromService(
          this.wardId,
          serviceSid,
          this.selectedPhoneNumber.sid
        );
        
        this.$toast.success('Phone number removed from service successfully');
        
        // Refresh phone number services
        await this.loadPhoneNumberServices();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to remove phone number from service. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to remove phone number from service");
        }
      } finally {
        this.isRemovingPhoneFromService = false;
      }
    },
    openEditPhoneNumberModal(phoneNumber) {
      // Populate form with current phone number data
      this.phoneFormData = {
        sms_url: phoneNumber.sms_url || '',
        sms_method: phoneNumber.sms_method || 'POST'
      };
      this.phoneFormErrors = {};
      this.selectedPhoneNumber = phoneNumber;
      
      // Show the modal
      this.$bvModal.show('edit-phone-number-modal');
    },
    useWardWebhook() {
      this.phoneFormData.sms_url = this.wardWebhookUrl;
    },
    async updatePhoneNumber() {
      // Validate form
      this.phoneFormErrors = {};
      let isValid = true;

      if (!this.phoneFormData.sms_url) {
        this.phoneFormErrors.sms_url = 'SMS URL is required';
        isValid = false;
      }

      if (!this.phoneFormData.sms_method) {
        this.phoneFormErrors.sms_method = 'SMS Method is required';
        isValid = false;
      }

      if (!isValid) {
        return;
      }

      if (!this.selectedPhoneNumber || !this.selectedPhoneNumber.sid) {
        handleError(new Error('No phone number selected'), 'Cannot update phone number');
        return;
      }

      this.isUpdatingPhoneNumber = true;
      this.credentialError = null;

      try {
        const updateData = {
          sms_url: this.phoneFormData.sms_url,
          sms_method: this.phoneFormData.sms_method
        };

        await WardApi.updateWardTwilioPhoneNumber(
          this.wardId,
          this.selectedPhoneNumber.sid,
          updateData
        );

        this.$toast.success('Phone number updated successfully');
        
        // Refresh phone numbers list
        await this.getTwilioPhoneNumbers();
        
        // Close modal
        this.$bvModal.hide('edit-phone-number-modal');
        this.resetEditPhoneNumberForm();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to update phone number. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to update phone number");
        }
      } finally {
        this.isUpdatingPhoneNumber = false;
      }
    },
    resetEditPhoneNumberForm() {
      this.phoneFormData = {
        sms_url: '',
        sms_method: 'POST'
      };
      this.phoneFormErrors = {};
      this.selectedPhoneNumber = null;
    },
    openPhoneNumberWizard() {
      this.phoneSearchStep = 0;
      this.phoneSearchForm = {
        country_code: 'US',
        area_code: null,
        contains: '',
        sms_enabled: true,
        limit: 20
      };
      this.phoneSearchResults = [];
      this.phoneSearchErrors = {};
      this.selectedPhoneForPurchase = null;
      this.purchasePhoneForm = {
        sms_url: this.wardWebhookUrl,
        sms_method: 'POST'
      };
      this.purchasePhoneErrors = {};
      this.showPhoneNumberWizard = true;
    },
    resetPhoneNumberWizard() {
      this.phoneSearchStep = 0;
      this.phoneSearchForm = {
        country_code: 'US',
        area_code: null,
        contains: '',
        sms_enabled: true,
        limit: 20
      };
      this.phoneSearchResults = [];
      this.phoneSearchErrors = {};
      this.selectedPhoneForPurchase = null;
      this.purchasePhoneForm = {
        sms_url: this.wardWebhookUrl,
        sms_method: 'POST'
      };
      this.purchasePhoneErrors = {};
      this.showPhoneNumberWizard = false;
    },
    goToPhoneSearchStep(step) {
      if (step === 0) {
        // Going back to search
        this.phoneSearchStep = 0;
        this.selectedPhoneForPurchase = null;
      } else if (step === 1 && this.phoneSearchResults.length > 0) {
        // Going to results step (only if we have results)
        this.phoneSearchStep = 1;
      }
    },
    async searchAvailablePhoneNumbers() {
      // Validate form
      this.phoneSearchErrors = {};
      let isValid = true;

      if (!this.phoneSearchForm.country_code) {
        this.phoneSearchErrors.country_code = 'Country code is required';
        isValid = false;
      }

      if (!isValid) {
        return;
      }

      this.isSearchingNumbers = true;
      this.credentialError = null;

      try {
        // Build search parameters
        const searchParams = {
          country_code: this.phoneSearchForm.country_code
        };
        
        if (this.phoneSearchForm.area_code) {
          searchParams.area_code = this.phoneSearchForm.area_code;
        }
        if (this.phoneSearchForm.contains) {
          searchParams.contains = this.phoneSearchForm.contains;
        }
        if (this.phoneSearchForm.sms_enabled !== null) {
          searchParams.sms_enabled = this.phoneSearchForm.sms_enabled;
        }
        if (this.phoneSearchForm.limit) {
          searchParams.limit = this.phoneSearchForm.limit;
        }

        const { data } = await WardApi.searchAvailablePhoneNumbers(this.wardId, searchParams);
        this.phoneSearchResults = data.phone_numbers || [];
        
        // Move to step 2 if we have results
        if (this.phoneSearchResults.length > 0) {
          this.phoneSearchStep = 1;
        } else {
          // Stay on step 1 but show message
          this.$toast.info('No phone numbers found. Try adjusting your search criteria.');
        }
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to search phone numbers. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else {
          handleError(e, "Error trying to search available phone numbers");
        }
      } finally {
        this.isSearchingNumbers = false;
      }
    },
    selectPhoneForPurchase(phone) {
      this.selectedPhoneForPurchase = phone;
      // Pre-fill SMS URL with ward webhook
      this.purchasePhoneForm.sms_url = this.wardWebhookUrl;
      this.purchasePhoneForm.sms_method = 'POST';
      this.purchasePhoneErrors = {};
    },
    useWardWebhookForPurchase() {
      this.purchasePhoneForm.sms_url = this.wardWebhookUrl;
    },
    async purchasePhoneNumber() {
      // Validate form
      this.purchasePhoneErrors = {};
      let isValid = true;

      if (!this.purchasePhoneForm.sms_url) {
        this.purchasePhoneErrors.sms_url = 'SMS URL is required';
        isValid = false;
      }

      if (!this.purchasePhoneForm.sms_method) {
        this.purchasePhoneErrors.sms_method = 'SMS Method is required';
        isValid = false;
      }

      if (!isValid) {
        return;
      }

      if (!this.selectedPhoneForPurchase || !this.selectedPhoneForPurchase.phone_number) {
        handleError(new Error('No phone number selected'), 'Cannot purchase phone number');
        return;
      }

      this.isPurchasingPhone = true;
      this.credentialError = null;

      try {
        const purchaseData = {
          phone_number: this.selectedPhoneForPurchase.phone_number,
          sms_url: this.purchasePhoneForm.sms_url,
          sms_method: this.purchasePhoneForm.sms_method
        };

        await WardApi.purchasePhoneNumber(this.wardId, purchaseData);

        this.$toast.success('Phone number purchased successfully');
        
        // Refresh phone numbers list
        await this.getTwilioPhoneNumbers();
        
        // Close wizard
        this.resetPhoneNumberWizard();
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to purchase phone number. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
        } else if (e.response && e.response.status === 400) {
          this.$toast.error(e.response.data.detail || 'This phone number is no longer available.');
        } else {
          handleError(e, "Error trying to purchase phone number");
        }
      } finally {
        this.isPurchasingPhone = false;
      }
    },
    addPhoneNumber(service) {
      // This method is now handled by openAddPhoneToServiceModal
      this.openAddPhoneToServiceModal(service);
    },
    connectCampaign(service) {
      // Open create campaign modal with service pre-selected
      this.openCreateCampaignModal(service);
    },
    openCreateCampaignModal(service = null) {
      // Open create campaign modal, optionally with service pre-selected
      if (service) {
        this.createCampaignForm.messaging_service_sid = service.sid;
      } else {
        this.createCampaignForm.messaging_service_sid = null;
      }
      this.createCampaignForm.description = 'Starter US A2P Campaign for testing or small-scale sending.';
      this.createCampaignForm.sample_messages = [
        'Twilio Starter Campaign. Messages may contain links https://www.twilio.com/messaging as customers build on Twilio.',
        'Twilio Starter campaign, Messages may contain callback numbers like +1400000000'
      ];
      this.createCampaignForm.opt_in_process = '';
      this.createCampaignErrors = {};
      this.showCreateCampaignModal = true;
    },
    async loadBrandCampaigns() {
      if (!this.selectedBrand || !this.selectedBrand.sid) {
        return;
      }
      
      this.isLoadingBrandCampaigns = true;
      try {
        const { data } = await WardApi.getWardTwilioBrandCampaigns(this.wardId, this.selectedBrand.sid);
        this.brandCampaigns = data.campaigns || [];
      } catch(e) {
        this.brandCampaigns = [];
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          // Credential error - already handled globally
        } else {
          handleError(e, "Error loading brand campaigns");
        }
      } finally {
        this.isLoadingBrandCampaigns = false;
      }
    },
    async loadServiceCampaigns() {
      if (!this.selectedService || !this.selectedService.sid) {
        return;
      }
      
      this.isLoadingServiceCampaigns = true;
      try {
        const { data } = await WardApi.getServiceCampaigns(this.wardId, this.selectedService.sid);
        this.serviceCampaigns = data.campaigns || [];
      } catch(e) {
        handleError(e, "Error trying to get service campaigns");
        this.serviceCampaigns = [];
      } finally {
        this.isLoadingServiceCampaigns = false;
      }
    },
    async loadServiceUsa2pResources() {
      if (!this.selectedService || !this.selectedService.sid) {
        return;
      }
      
      this.isLoadingServiceUsa2p = true;
      try {
        const { data } = await WardApi.getWardTwilioUsa2pResources(this.wardId, this.selectedService.sid);
        this.serviceUsa2pResources = data.compliance || [];
      } catch(e) {
        handleError(e, "Error trying to get service usa2p resources");
        this.serviceUsa2pResources = [];
      } finally {
        this.isLoadingServiceUsa2p = false;
      }
    },
    openCreateUsa2pModal(service = null, brand = null) {
      // Reset form
      this.createUsa2pForm = {
        brand_registration_sid: brand ? brand.sid : null,
        messaging_service_sid: service ? service.sid : null,
        description: 'Starter US A2P Campaign for testing or small-scale sending.',
        message_samples: [
          'Twilio Starter Campaign. Messages may contain links https://www.twilio.com/messaging as customers build on Twilio.',
          'Twilio Starter campaign, Messages may contain callback numbers like +1400000000'
        ],
        us_app_to_person_usecase: 'SOLE_PROPRIETOR'
      };
      this.createUsa2pErrors = {};
      this.showCreateUsa2pModal = true;
    },
    async createUsa2pResource() {
      // Validate form
      this.createUsa2pErrors = {};
      let hasErrors = false;
      
      if (!this.createUsa2pForm.brand_registration_sid) {
        this.createUsa2pErrors.brand_registration_sid = 'Brand registration is required';
        hasErrors = true;
      }
      
      if (!this.createUsa2pForm.messaging_service_sid) {
        this.createUsa2pErrors.messaging_service_sid = 'Messaging service is required';
        hasErrors = true;
      }
      
      if (!this.createUsa2pForm.description || this.createUsa2pForm.description.trim().length < 40) {
        this.createUsa2pErrors.description = 'Description is required and must be at least 40 characters';
        hasErrors = true;
      }
      
      if (!this.createUsa2pForm.message_samples || this.createUsa2pForm.message_samples.length < 2) {
        this.createUsa2pErrors.message_samples = 'At least 2 sample messages are required';
        hasErrors = true;
      }
      
      // Validate each sample message
      const emptyMessages = this.createUsa2pForm.message_samples.filter(msg => !msg || !msg.trim() || msg.trim().length < 20);
      if (emptyMessages.length > 0) {
        this.createUsa2pErrors.message_samples = 'All sample messages must have at least 20 characters';
        hasErrors = true;
      }
      
      if (hasErrors) {
        return;
      }
      
      this.isCreatingUsa2p = true;
      try {
        const usa2pData = {
          brand_registration_sid: this.createUsa2pForm.brand_registration_sid,
          description: this.createUsa2pForm.description.trim(),
          message_samples: this.createUsa2pForm.message_samples.filter(msg => msg && msg.trim()),
          us_app_to_person_usecase: this.createUsa2pForm.us_app_to_person_usecase
        };
        
        await WardApi.createWardTwilioUsa2p(this.wardId, this.createUsa2pForm.messaging_service_sid, usa2pData);
        
        this.$bvToast.toast('Usa2p resource created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Close modal and refresh data
        this.showCreateUsa2pModal = false;
        this.resetCreateUsa2pForm();
        
        // Refresh usa2p resources if viewing service details
        if (this.selectedService && this.showServiceUsa2p) {
          await this.loadServiceUsa2pResources();
        }
        
        // Refresh brand registrations list
        await this.getTwilioBrandRegistrations();
      } catch(e) {
        handleError(e, "Error creating usa2p resource");
      } finally {
        this.isCreatingUsa2p = false;
      }
    },
    resetCreateUsa2pForm() {
      this.createUsa2pForm = {
        brand_registration_sid: null,
        messaging_service_sid: null,
        description: '',
        message_samples: ['', ''],
        us_app_to_person_usecase: 'SOLE_PROPRIETOR'
      };
      this.createUsa2pErrors = {};
    },
    viewUsa2pDetails(usa2p) {
      this.selectedUsa2p = usa2p;
      this.$bvModal.show('usa2p-details-modal');
    },
    async deleteUsa2pResource(usa2p) {
      if (!usa2p || !usa2p.sid || !usa2p.messaging_service_sid) {
        return;
      }
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this usa2p resource? This will block all A2P 10DLC messaging from the messaging service. This action cannot be undone.',
        {
          title: 'Delete Usa2p Resource',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true
        }
      );
      
      if (!confirmed) {
        return;
      }
      
      this.isDeletingUsa2p = true;
      try {
        await WardApi.deleteWardTwilioUsa2pResource(this.wardId, usa2p.messaging_service_sid, usa2p.sid);
        
        this.$bvToast.toast('Usa2p resource deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Refresh usa2p resources if viewing service details
        if (this.selectedService && this.showServiceUsa2p) {
          await this.loadServiceUsa2pResources();
        }
      } catch(e) {
        handleError(e, "Error deleting usa2p resource");
      } finally {
        this.isDeletingUsa2p = false;
      }
    },
    getUsa2pStatusVariant(status) {
      if (!status) return 'secondary';
      const statusUpper = status.toUpperCase();
      if (statusUpper === 'VERIFIED') return 'success';
      if (statusUpper === 'PENDING' || statusUpper === 'IN_PROGRESS') return 'warning';
      if (statusUpper === 'FAILED') return 'danger';
      return 'secondary';
    },
    async createCampaign() {
      // Validate form
      this.createCampaignErrors = {};
      let hasErrors = false;
      
      if (!this.createCampaignForm.messaging_service_sid) {
        this.createCampaignErrors.messaging_service_sid = 'Messaging service is required';
        hasErrors = true;
      }
      
      if (!this.createCampaignForm.description || this.createCampaignForm.description.trim().length < 10) {
        this.createCampaignErrors.description = 'Description is required and must be at least 10 characters';
        hasErrors = true;
      }
      
      if (!this.createCampaignForm.sample_messages || this.createCampaignForm.sample_messages.length < 2) {
        this.createCampaignErrors.sample_messages = 'At least 2 sample messages are required';
        hasErrors = true;
      }
      
      // Validate each sample message
      const emptyMessages = this.createCampaignForm.sample_messages.filter(msg => !msg || !msg.trim());
      if (emptyMessages.length > 0) {
        this.createCampaignErrors.sample_messages = 'All sample messages must have content';
        hasErrors = true;
      }
      
      if (!this.createCampaignForm.opt_in_process || this.createCampaignForm.opt_in_process.trim().length < 10) {
        this.createCampaignErrors.opt_in_process = 'Opt-in process description is required and must be at least 10 characters';
        hasErrors = true;
      }
      
      if (hasErrors) {
        return;
      }
      
      this.isCreatingCampaign = true;
      try {
        const campaignData = {
          messaging_service_sid: this.createCampaignForm.messaging_service_sid,
          description: this.createCampaignForm.description.trim(),
          sample_messages: this.createCampaignForm.sample_messages.filter(msg => msg && msg.trim()),
          opt_in_process: this.createCampaignForm.opt_in_process.trim()
        };
        
        await WardApi.createWardTwilioSoleProprietorshipCampaign(this.wardId, campaignData);
        
        this.$bvToast.toast('Sole proprietorship campaign created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Close modal and refresh data
        this.showCreateCampaignModal = false;
        this.resetCreateCampaignForm();
        
        // Refresh campaigns list
        await this.getTwilioCampaigns();
        
        // Refresh service campaigns if viewing service details
        if (this.selectedService && this.showServiceCampaigns) {
          await this.loadServiceCampaigns();
        }
        
        // Refresh brand campaigns if viewing brand details
        if (this.selectedBrand && this.showBrandCampaigns) {
          await this.loadBrandCampaigns();
        }
        
        // Refresh brand registrations list
        await this.getTwilioBrandRegistrations();
      } catch(e) {
        handleError(e, "Error creating campaign");
      } finally {
        this.isCreatingCampaign = false;
      }
    },
    resetCreateCampaignForm() {
      this.createCampaignForm = {
        messaging_service_sid: null,
        description: 'Starter US A2P Campaign for testing or small-scale sending.',
        sample_messages: [
          'Twilio Starter Campaign. Messages may contain links https://www.twilio.com/messaging as customers build on Twilio.',
          'Twilio Starter campaign, Messages may contain callback numbers like +1400000000'
        ],
        opt_in_process: ''
      };
      this.createCampaignErrors = {};
    },
    addSampleMessage() {
      this.createCampaignForm.sample_messages.push('');
    },
    removeSampleMessage(index) {
      if (this.createCampaignForm.sample_messages.length > 2) {
        this.createCampaignForm.sample_messages.splice(index, 1);
      } else {
        this.$bvToast.toast('At least 2 sample messages are required', {
          title: 'Validation Error',
          variant: 'warning',
          solid: true
        });
      }
    },
    openCreateBrandRegistrationModal() {
      // Reset form
      this.createBrandRegistrationForm = {
        customer_profile_bundle_sid: null,
        a2p_profile_bundle_sid: null,
        brand_type: null,
        mock: false,
        skip_automatic_sec_vet: false
      };
      this.createBrandRegistrationErrors = {};
      this.showCreateBrandRegistrationModal = true;
    },
    async createBrandRegistration() {
      // Validate form
      this.createBrandRegistrationErrors = {};
      let hasErrors = false;
      
      if (!this.createBrandRegistrationForm.customer_profile_bundle_sid || !this.createBrandRegistrationForm.customer_profile_bundle_sid.trim()) {
        this.createBrandRegistrationErrors.customer_profile_bundle_sid = 'Customer profile bundle SID is required';
        hasErrors = true;
      } else if (!this.createBrandRegistrationForm.customer_profile_bundle_sid.trim().startsWith('BU') || this.createBrandRegistrationForm.customer_profile_bundle_sid.trim().length !== 34) {
        this.createBrandRegistrationErrors.customer_profile_bundle_sid = 'Bundle SID must start with BU and be 34 characters long';
        hasErrors = true;
      }
      
      if (this.createBrandRegistrationForm.a2p_profile_bundle_sid && this.createBrandRegistrationForm.a2p_profile_bundle_sid.trim()) {
        if (!this.createBrandRegistrationForm.a2p_profile_bundle_sid.trim().startsWith('BU') || this.createBrandRegistrationForm.a2p_profile_bundle_sid.trim().length !== 34) {
          this.createBrandRegistrationErrors.a2p_profile_bundle_sid = 'A2P profile bundle SID must start with BU and be 34 characters long';
          hasErrors = true;
        }
      }
      
      if (hasErrors) {
        return;
      }
      
      this.isCreatingBrandRegistration = true;
      try {
        const brandData = {
          customer_profile_bundle_sid: this.createBrandRegistrationForm.customer_profile_bundle_sid.trim(),
        };
        
        // Add optional fields if provided
        if (this.createBrandRegistrationForm.a2p_profile_bundle_sid && this.createBrandRegistrationForm.a2p_profile_bundle_sid.trim()) {
          brandData.a2p_profile_bundle_sid = this.createBrandRegistrationForm.a2p_profile_bundle_sid.trim();
        }
        if (this.createBrandRegistrationForm.brand_type) {
          brandData.brand_type = this.createBrandRegistrationForm.brand_type;
        }
        if (this.createBrandRegistrationForm.mock !== null && this.createBrandRegistrationForm.mock !== undefined) {
          brandData.mock = this.createBrandRegistrationForm.mock;
        }
        if (this.createBrandRegistrationForm.skip_automatic_sec_vet !== null && this.createBrandRegistrationForm.skip_automatic_sec_vet !== undefined) {
          brandData.skip_automatic_sec_vet = this.createBrandRegistrationForm.skip_automatic_sec_vet;
        }
        
        await WardApi.createWardTwilioBrandRegistration(this.wardId, brandData);
        
        this.$bvToast.toast('Brand registration created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Close modal and refresh data
        this.showCreateBrandRegistrationModal = false;
        this.resetCreateBrandRegistrationForm();
        
        // Refresh brand registrations list
        await this.getTwilioBrandRegistrations();
      } catch(e) {
        handleError(e, "Error creating brand registration");
      } finally {
        this.isCreatingBrandRegistration = false;
      }
    },
    resetCreateBrandRegistrationForm() {
      this.createBrandRegistrationForm = {
        customer_profile_bundle_sid: null,
        a2p_profile_bundle_sid: null,
        brand_type: null,
        mock: false,
        skip_automatic_sec_vet: false
      };
      this.createBrandRegistrationErrors = {};
    },
    viewBrandDetails(brand) {
      this.selectedBrand = brand;
      this.$bvModal.show('brand-details-modal');
    },
    viewCampaignDetails(campaign) {
      this.selectedCampaign = campaign;
      this.$bvModal.show('campaign-details-modal');
    },
    async deleteCampaign() {
      if (!this.selectedCampaign || !this.selectedCampaign.sid) {
        return;
      }
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to delete this campaign? This action cannot be undone.',
        {
          title: 'Delete Campaign',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true
        }
      );
      
      if (!confirmed) {
        return;
      }
      
      this.isDeletingCampaign = true;
      try {
        await WardApi.deleteWardTwilioCampaign(this.wardId, this.selectedCampaign.sid);
        
        this.$bvToast.toast('Campaign deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Close modal and refresh
        this.$bvModal.hide('campaign-details-modal');
        this.selectedCampaign = null;
        
        // Refresh campaigns list
        await this.getTwilioCampaigns();
        
        // Refresh service campaigns if viewing service details
        if (this.selectedService && this.showServiceCampaigns) {
          await this.loadServiceCampaigns();
        }
        
        // Refresh brand campaigns if viewing brand details
        if (this.selectedBrand && this.showBrandCampaigns) {
          await this.loadBrandCampaigns();
        }
        
        // Refresh brand registrations list
        await this.getTwilioBrandRegistrations();
      } catch(e) {
        handleError(e, "Error deleting campaign");
      } finally {
        this.isDeletingCampaign = false;
      }
    },
    async resubmitCampaign() {
      if (!this.selectedCampaign || !this.selectedCampaign.sid) {
        return;
      }
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Resubmit this campaign for approval?',
        {
          title: 'Resubmit Campaign',
          okVariant: 'primary',
          okTitle: 'Resubmit',
          cancelTitle: 'Cancel',
          centered: true
        }
      );
      
      if (!confirmed) {
        return;
      }
      
      this.isResubmittingCampaign = true;
      try {
        const { data } = await WardApi.resubmitWardTwilioCampaign(this.wardId, this.selectedCampaign.sid);
        this.selectedCampaign = data;
        
        this.$bvToast.toast('Campaign resubmitted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        
        // Refresh campaigns list
        await this.getTwilioCampaigns();
        
        // Refresh service campaigns if viewing service details
        if (this.selectedService && this.showServiceCampaigns) {
          await this.loadServiceCampaigns();
        }
        
        // Refresh brand campaigns if viewing brand details
        if (this.selectedBrand && this.showBrandCampaigns) {
          await this.loadBrandCampaigns();
        }
        
        // Refresh brand registrations list
        await this.getTwilioBrandRegistrations();
      } catch(e) {
        handleError(e, "Error resubmitting campaign");
      } finally {
        this.isResubmittingCampaign = false;
      }
    },
    toggleBrandRow(brand, index) {
      const brandSid = brand.sid;
      const isExpanded = this.isBrandRowExpanded(brandSid);
      
      if (isExpanded) {
        this.expandedBrandRows.delete(brandSid);
        // Update the table to collapse the row
        this.$set(this.brands_table.items[index], '_showDetails', false);
      } else {
        this.expandedBrandRows.add(brandSid);
        // Update the table to expand the row
        this.$set(this.brands_table.items[index], '_showDetails', true);
        // Load campaigns if not already loaded
        if (!this.brandCampaignsByRow[brandSid]) {
          this.loadBrandCampaignsForRow(brandSid);
        }
      }
    },
    isBrandRowExpanded(brandSid) {
      return this.expandedBrandRows.has(brandSid);
    },
    getBrandCampaignsForRow(brandSid) {
      return this.brandCampaignsByRow[brandSid] || [];
    },
    isLoadingBrandCampaignsForRow(brandSid) {
      return this.isLoadingBrandCampaignsByRow[brandSid] || false;
    },
    async loadBrandCampaignsForRow(brandSid) {
      if (!brandSid) {
        return;
      }
      
      this.$set(this.isLoadingBrandCampaignsByRow, brandSid, true);
      try {
        const { data } = await WardApi.getWardTwilioBrandCampaigns(this.wardId, brandSid);
        this.$set(this.brandCampaignsByRow, brandSid, data.campaigns || []);
      } catch(e) {
        this.$set(this.brandCampaignsByRow, brandSid, []);
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          // Credential error - already handled globally
        } else {
          handleError(e, "Error loading brand campaigns");
        }
      } finally {
        this.$set(this.isLoadingBrandCampaignsByRow, brandSid, false);
      }
    },
    getBrandRowClass(item, type) {
      if (!item || type !== 'row') return '';
      if (this.isBrandRowExpanded(item.sid)) {
        return 'table-active';
      }
      return '';
    },
    getBrandStatusVariant(status) {
      if (!status) return 'secondary';
      const statusUpper = status.toUpperCase();
      if (statusUpper === 'APPROVED') return 'success';
      if (statusUpper === 'PENDING' || statusUpper === 'IN_REVIEW') return 'warning';
      if (statusUpper === 'FAILED' || statusUpper === 'DELETION_FAILED' || statusUpper === 'SUSPENDED') return 'danger';
      if (statusUpper === 'DELETION_PENDING') return 'info';
      return 'secondary';
    },
    getCampaignStatusVariant(status) {
      if (!status) return 'secondary';
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved' || statusLower === 'twilio-approved') {
        return 'success';
      } else if (statusLower === 'pending' || statusLower === 'pending-review') {
        return 'warning';
      } else if (statusLower === 'rejected' || statusLower === 'failed') {
        return 'danger';
      }
      return 'info';
    },
    formatDate(dateString) {
      if (!dateString) {
        return 'N/A';
      }
      try {
        // Try to parse and format the date
        const momentDate = this.$moment(dateString);
        if (momentDate.isValid()) {
          return momentDate.local().format('llll');
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    },
    openCreateServiceModal() {
      this.resetCreateServiceForm();
      this.$bvModal.show('create-service-modal');
    },
    resetCreateServiceForm() {
      this.serviceFormData = {
        friendly_name: ''
      };
      this.serviceFormErrors = {};
      this.isCreatingService = false;
    },
    async createService() {
      // Validate form
      this.serviceFormErrors = {};
      if (!this.serviceFormData.friendly_name || this.serviceFormData.friendly_name.trim() === '') {
        this.serviceFormErrors.friendly_name = 'Friendly name is required';
        return;
      }

      this.isCreatingService = true;
      this.credentialError = null;

      try {
        const response = await WardApi.createWardTwilioService(this.wardId, {
          friendly_name: this.serviceFormData.friendly_name.trim()
        });
        
        // Refresh services list
        await this.getTwilioServices();
        
        // Close the create modal
        this.$bvModal.hide('create-service-modal');
        
        // Show the created service details
        const createdService = response.data;
        this.selectedService = createdService;
        this.$bvModal.show('service-details-modal');
        
        // Show success message
        this.$bvToast.toast('Messaging service created successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch(e) {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) {
          this.credentialError = 'Unable to create messaging service. The Twilio Account SID and Auth Token have not been configured for this ward, or the credentials are invalid.';
          this.$bvToast.toast('Twilio credentials are missing or invalid', {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        } else {
          const errorMessage = e.response && e.response.data && e.response.data.detail 
            ? e.response.data.detail 
            : 'Error creating messaging service. Please try again.';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
          handleError(e, "Error creating messaging service");
        }
      } finally {
        this.isCreatingService = false;
      }
    }
  }
}
</script>
